import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getProjectImageDataUrl } from "@/lib/imageCache";
import { processImageFile } from "@/lib/imageUtils";
import {
  resolveLayout, findNextEmpty, makeEmptyPage, emptyCell, exportPDF, printPDF,
} from "@/lib/pinLayout";
import { PinCanvas } from "@/components/PinCanvas";
import { MiniPage } from "@/components/MiniPage";
import { Inspector, TextStyleControls } from "@/components/Inspector";
import { ExpiryBadge } from "@/components/ExpiryBadge";
import { CropDialog } from "@/components/CropDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft, Upload, FilePlus2, Download, Printer, ZoomIn, ZoomOut,
  CircleDot, Loader2, Check, CloudOff, Layers, Save, Ruler, Trash2, Type, CopyPlus, Eraser,
  Undo2, Redo2, ChevronLeft, ChevronRight, ChevronDown, Search, Settings, Palette, Bell, MoreHorizontal,
} from "lucide-react";

const BASE_PPMM = 2.55;

function NumberStepper({
  id,
  value,
  min,
  max,
  step,
  onChange,
  dataTestId,
  theme = "dark",
}) {
  const current = Number(value);
  const safeCurrent = Number.isFinite(current) ? current : min;
  const safeMax =
    max === undefined || max === null
      ? Number.POSITIVE_INFINITY
      : Number(max);
  const isDark = theme === "dark";

  const adjust = (direction) => {
    const next = Math.round(
      (safeCurrent + direction * step) * 1000
    ) / 1000;

    onChange(Math.min(safeMax, Math.max(min, next)));
  };

  return (
    <div className={`flex h-11 w-full items-center overflow-hidden rounded-xl border ${
      isDark
        ? "border-[#29466f] bg-[#0b1733]"
        : "border-slate-300 bg-white"
    }`}>
      <button
        type="button"
        aria-label={`Kurangi ${id}`}
        disabled={safeCurrent <= min}
        onClick={() => adjust(-1)}
        className={`flex h-full w-12 shrink-0 items-center justify-center text-2xl font-semibold transition disabled:opacity-35 ${
          isDark
            ? "text-slate-300 hover:bg-white/5"
            : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        −
      </button>

      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        data-testid={dataTestId}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`h-full min-w-0 flex-1 !rounded-none !border-0 !bg-transparent px-1 text-center text-xl font-semibold shadow-none [appearance:textfield] focus-visible:!ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${
          isDark ? "text-slate-100" : "text-slate-900"
        }`}
      />

      <button
        type="button"
        aria-label={`Tambah ${id}`}
        disabled={safeCurrent >= safeMax}
        onClick={() => adjust(1)}
        className={`flex h-full w-12 shrink-0 items-center justify-center text-2xl font-semibold transition disabled:opacity-35 ${
          isDark
            ? "text-slate-300 hover:bg-white/5"
            : "text-slate-700 hover:bg-slate-100"
        }`}
      >
        +
      </button>
    </div>
  );
}

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [language] = useState(() => localStorage.getItem("pinmaker-login-language") || "id");
  const isEnglish = language === "en";
  const [pages, setPages] = useState([makeEmptyPage(15)]);
  const [currentPage, setCurrentPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState("saved"); // saved|saving|dirty|error
  const [autoSaveOn, setAutoSaveOn] = useState(() => localStorage.getItem("pinmaker_autosave") !== "off");
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [settingsGearRotation, setSettingsGearRotation] = useState(0);
  const [orderPromptEnabled, setOrderPromptEnabled] = useState(
    () => localStorage.getItem("pinmaker_editor_order_prompt") === "on"
  );
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);


  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);


  useEffect(() => {
    let active = true;

    const loadNotifications = async () => {
      try {
        const response = await api.get("/customer/notifications");
        if (!active) return;
        setNotifications(response.data.notifications || []);
        setUnreadNotificationCount(response.data.unread_count || 0);
      } catch (error) {
        // Jangan mengganggu pekerjaan editor jika notifikasi gagal dimuat.
        if (error?.response?.status !== 401) {
          console.error("Gagal memuat notifikasi pesanan:", error);
        }
      }
    };

    loadNotifications();
    const timer = setInterval(loadNotifications, 30000);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const clearLocalNotifications = () => {
      setNotifications([]);
      setUnreadNotificationCount(0);
    };

    const handleStorage = (event) => {
      if (event.key === "pinmaker-notifications-cleared") {
        clearLocalNotifications();
      }
    };

    window.addEventListener("pinmaker-notifications-cleared", clearLocalNotifications);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("pinmaker-notifications-cleared", clearLocalNotifications);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const clearAllNotifications = async () => {
    if (!notifications.length) return;
    if (!window.confirm(isEnglish
      ? "Clear all notifications? Payment orders and API Keys will remain unchanged."
      : "Bersihkan semua notifikasi? Pesanan pembayaran dan API Key tidak akan berubah.")) return;
    try {
      await api.post("/customer/notifications/clear-all");
      setNotifications([]);
      setUnreadNotificationCount(0);
      window.dispatchEvent(new Event("pinmaker-notifications-cleared"));
      try {
        localStorage.setItem("pinmaker-notifications-cleared", String(Date.now()));
      } catch (storageError) {
        console.warn("Gagal menyinkronkan notifikasi antartab:", storageError);
      }
      toast.success(isEnglish ? "All notifications cleared." : "Semua notifikasi berhasil dibersihkan.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (isEnglish ? "Could not clear notifications." : "Notifikasi gagal dibersihkan."));
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(
        `/customer/notifications/${notificationId}/read`
      );
      const readAt = new Date().toISOString();

      setNotifications((items) => items.map((item) =>
        item.id === notificationId ? { ...item, read_at: readAt } : item
      ));
      setUnreadNotificationCount((count) => Math.max(0, count - 1));
    } catch (error) {
      toast.error(isEnglish
        ? "Could not mark notification as read"
        : "Notifikasi gagal ditandai sudah dibaca");
    }
  };

  const resumePendingRenewalPayment = (item) => {
    if (!item?.redirect_url) {
      toast.error(isEnglish ? "Payment link is unavailable." : "Tautan pembayaran tidak tersedia.");
      return;
    }
    window.open(item.redirect_url, "_blank", "noopener,noreferrer");
  };

  const cancelPendingRenewalPayment = async (item) => {
    if (!item?.order_id) return;
    const confirmed = window.confirm(isEnglish
      ? "Cancel this pending payment? You can choose a package again after Midtrans confirms the cancellation."
      : "Batalkan pesanan yang belum dibayar ini? Kamu dapat memilih paket kembali setelah Midtrans mengonfirmasi pembatalan.");
    if (!confirmed) return;
    try {
      await api.post(`/customer/renewal-orders/${encodeURIComponent(item.order_id)}/cancel`);
      const refreshed = await api.get("/customer/notifications");
      setNotifications(refreshed.data.notifications || []);
      setUnreadNotificationCount(refreshed.data.unread_count || 0);
      toast.success(isEnglish ? "Pending order cancelled." : "Pesanan Pending berhasil dibatalkan.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || (isEnglish
        ? "Midtrans has not confirmed cancellation. Do not create another payment yet."
        : "Midtrans belum mengonfirmasi pembatalan. Jangan membuat pembayaran baru dulu."));
    }
  };

  const [autoSaveSettingsOpen, setAutoSaveSettingsOpen] = useState(false);
  const [orderPromptSettingsOpen, setOrderPromptSettingsOpen] = useState(false);
  const [editorThemeSettingsOpen, setEditorThemeSettingsOpen] = useState(false);
  const [editorTheme, setEditorTheme] = useState(() => localStorage.getItem("pinmaker-login-theme") || "light");
  const [proFeatureEnabled, setProFeatureEnabled] = useState(null);

  useEffect(() => {
    let active = true;

    api.get("/user/feature-access")
      .then((res) => {
        if (active) {
          setProFeatureEnabled(
            Boolean(res.data?.pro_feature_enabled)
          );
        }
      })
      .catch(() => {
        if (active) setProFeatureEnabled(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [viewScale, setViewScale] = useState(1);
  const [busy, setBusy] = useState(false);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  // order-number prompt shown automatically after an image batch is placed
  const [orderPromptOpen, setOrderPromptOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  // Upload banyak folder dengan nomor pesanan otomatis.
  const folderInputRef = useRef(null);
  const [folderUploadDialogOpen, setFolderUploadDialogOpen] = useState(false);
  const [folderUploadGroups, setFolderUploadGroups] = useState([]);
  const [folderUploadInfoMode, setFolderUploadInfoMode] = useState("order");
  const [folderUploadNoticeOpen, setFolderUploadNoticeOpen] = useState(false);
  const [lastBatchCount, setLastBatchCount] = useState(0);
  // export/print page selection
  const [outputDialogOpen, setOutputDialogOpen] = useState(false);
  const [outputMode, setOutputMode] = useState("export"); // export | print
  const [selectedPageSet, setSelectedPageSet] = useState(new Set());
  // circle size settings (per project)
  const [gridPreset, setGridPreset] = useState("44");
  const [innerD, setInnerD] = useState(45);
  const [outerD, setOuterD] = useState(55.5);
  const [gapH, setGapH] = useState(0);
  const [gapV, setGapV] = useState(3);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [presetEditorOpen, setPresetEditorOpen] = useState(false);
  const [showCenter, setShowCenter] = useState(false);
  const [centerDotSize, setCenterDotSize] = useState(2.4);
  const [centerDotColor, setCenterDotColor] = useState("#e22020");
  // global default text style (all text follows this)
  const [defaultFont, setDefaultFont] = useState({ fontFamily: "sans", bold: true, color: "#111111" });
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  // delete-page confirm
  const [deletePageIdx, setDeletePageIdx] = useState(null);
  // auto-fill (penuhi) empty cells by duplicating existing images
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillPageSet, setFillPageSet] = useState(new Set());
  const [fillSource, setFillSource] = useState("last"); // "last" | "all"
  const [fillAllPositions, setFillAllPositions] = useState(true);
  const [fillPositionSet, setFillPositionSet] = useState(new Set());
  // crop dialog
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  // duplicate selected image dialog
  const [dupOpen, setDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState("5");
  // clear (kosongkan) all content on pages
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearPageSet, setClearPageSet] = useState(new Set());
  // bulk delete pages (multi-select)
  const [deletePagesDialogOpen, setDeletePagesDialogOpen] = useState(false);
  const [deletePagesSet, setDeletePagesSet] = useState(new Set());
  // undo/redo availability (mirrors the ref stacks for button disabled state)
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [showLeftRail, setShowLeftRail] = useState(() => localStorage.getItem("pinmaker_leftrail") !== "off");
  const [searchQ, setSearchQ] = useState("");

  // Search text cells across all pages -> list of {pageIdx, cellIdx, text}
  const searchResults = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    pages.forEach((pg, pi) => pg.cells.forEach((c, ci) => {
      if (c.type === "text" && c.text && c.text.toLowerCase().includes(q)) {
        out.push({ pageIdx: pi, cellIdx: ci, text: c.text });
      }
    }));
    return out.slice(0, 50);
  }, [searchQ, pages]);
  const canvasScrollRef = useRef(null);
  const wheelCooldown = useRef(0);

  const pagesRef = useRef(pages);
  const nameRef = useRef(name);
  const settingsRef = useRef({ gridPreset: "44", innerD: 45, outerD: 55.5, gapH: 0, gapV: 3, showCenter: false, centerDotSize: 1.5, centerDotColor: "#e22020", defaultFont: { fontFamily: "sans", bold: true, color: "#111111" } });
  const saveTimer = useRef(null);
  const skipAutosave = useRef(true);
  const autoSaveRef = useRef(true);
  const saveStatusRef = useRef("saved");
  const internalUpdate = useRef(false);
  const fileInputRef = useRef(null);
  // undo/redo history (snapshots of pages)
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const lastSnap = useRef(null);
  const applyingHistory = useRef(false);

  useEffect(() => { pagesRef.current = pages; }, [pages]);
  useEffect(() => { nameRef.current = name; }, [name]);
  useEffect(() => { saveStatusRef.current = saveStatus; }, [saveStatus]);
  useEffect(() => {
    autoSaveRef.current = autoSaveOn;
  }, [autoSaveOn]);

  // Load account-specific preferences without overwriting the open project's settings.
  useEffect(() => {
    let mounted = true;
    api.get("/user/settings").then(({ data }) => {
      if (!mounted) return;
      if (typeof data.autoSave === "boolean") {
        setAutoSaveOn(data.autoSave);
        autoSaveRef.current = data.autoSave;
      }
      if (data.theme === "light" || data.theme === "dark") {
        setEditorTheme(data.theme);
        localStorage.setItem("pinmaker-login-theme", data.theme);
        document.documentElement.classList.toggle("dark", data.theme === "dark");
      }
    }).catch(() => {
      if (mounted) toast.error(isEnglish ? "Failed to load account settings" : "Pengaturan akun gagal dimuat");
    });
    return () => { mounted = false; };
  }, [isEnglish]);

  // Warn before closing tab/window when there are unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveStatusRef.current === "dirty" || saveStatusRef.current === "error") {
        e.preventDefault();
        e.returnValue = isEnglish ? "You have unsaved changes" : "File belum disimpan";
        return isEnglish ? "You have unsaved changes" : "File belum disimpan";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEnglish]);
  useEffect(() => { settingsRef.current = { gridPreset, innerD, outerD, gapH, gapV, showCenter, centerDotSize, centerDotColor, defaultFont }; }, [gridPreset, innerD, outerD, gapH, gapV, showCenter, centerDotSize, centerDotColor, defaultFont]);

  const layout = useMemo(() => resolveLayout(innerD, outerD, gridPreset, gapH, gapV), [innerD, outerD, gridPreset, gapH, gapV]);


  // Reflow all cells when the outer diameter changes, preserving their order.
  const reflowPages = (sourcePages, perPage) => {
    const flat = sourcePages.flatMap((page) => page.cells);
    while (flat.length && flat[flat.length - 1]?.type === "empty") flat.pop();
    const pageCount = Math.max(1, Math.ceil(flat.length / perPage));
    return Array.from({ length: pageCount }, (_, p) => ({
      cells: Array.from({ length: perPage }, (_, i) => {
        const cell = flat[p * perPage + i];
        return cell ? { ...cell, index: i } : emptyCell(i);
      }),
    }));
  };

  const ppmm = BASE_PPMM * viewScale;

  // Reflow after loading finishes and whenever outer diameter changes.
  const lastReflowCount = useRef(null);
  useEffect(() => {
    if (loading || skipAutosave.current) return;
    const count = layout.CELLS_PER_PAGE;
    if (lastReflowCount.current === count &&
        pages.every((page) => page.cells.length === count)) return;

    const migrated = reflowPages(pagesRef.current, count);
    lastReflowCount.current = count;
    if (migrated.some((page, i) =>
      page.cells.length !== pagesRef.current[i]?.cells.length
    ) || migrated.length !== pagesRef.current.length) {
      pagesRef.current = migrated;
      setPages(migrated);
      setCurrentPage((current) => Math.min(current, migrated.length - 1));
      setSelected(null);
    }
  }, [loading, layout.CELLS_PER_PAGE, pages]);


  // ---- load ----
  useEffect(() => {
    let mounted = true;
    const loadingToastId = toast.loading(
      isEnglish ? "Loading project…" : "Memuat proyek…"
    );
    (async () => {
      setLoading(true);
      try {
        const loadStartedAt = performance.now();
        const res = await api.get(`/projects/${id}`);
        if (!mounted) return;
        console.info("[Load timing] API:", Math.round(performance.now() - loadStartedAt), "ms");
        const mappingStartedAt = performance.now();
        const loadedPages = (res.data.pages || []).map((pg) => ({
          cells: pg.cells.map((c) => ({
            index: c.index,
            type: c.type,
            imageId: c.imageId || null,
            imageData: c.imageData || null,
            text: c.text || null,
            fillColor: c.fillColor || null,
            bgColor: c.bgColor || null,
            zoom: c.zoom ?? 1,
            offX: c.offX ?? 0,
            offY: c.offY ?? 0,
            fontFamily: c.fontFamily || "sans",
            bold: c.bold ?? true,
            color: c.color || "#111111",
          })),
        }));
        console.info("[Load timing] Mapping:", Math.round(performance.now() - mappingStartedAt), "ms",
          "| pages:", loadedPages.length,
          "| cells:", loadedPages.reduce((sum, page) => sum + page.cells.length, 0));
        setName(res.data.name || "");
        const s = res.data.settings || {};
        const params = new URLSearchParams(window.location.search);
        const requestedInnerD = Number(params.get("innerD"));
        const requestedOuterD = Number(params.get("outerD"));
        const requestedGridPreset = params.get("gridPreset");
        const requestedGapH = Number(params.get("gapH"));
        const requestedGapV = Number(params.get("gapV"));
        const requestedShowCenter = params.get("showCenter");
        const requestedCenterDotSize = Number(params.get("centerDotSize"));
        const requestedCenterDotColor = params.get("centerDotColor");
        const hasPreset = params.has("innerD") && params.has("outerD")
          && Number.isFinite(requestedInnerD) && requestedInnerD > 0
          && Number.isFinite(requestedOuterD) && requestedOuterD > requestedInnerD;
        const validPresets = ["25", "32", "44", "58", "75"];
        const nextGridPreset = validPresets.includes(requestedGridPreset)
          ? requestedGridPreset
          : (validPresets.includes(String(s.gridPreset)) ? String(s.gridPreset) : "44");
        setGridPreset(nextGridPreset);
        setInnerD(hasPreset ? requestedInnerD : (s.innerD ?? 45));
        setOuterD(hasPreset ? requestedOuterD : (s.outerD ?? 55.5));
        setGapH(
          params.has("gapH") && Number.isFinite(requestedGapH) && requestedGapH >= 0
            ? requestedGapH : (s.gapH ?? 0)
        );
        setGapV(
          params.has("gapV") && Number.isFinite(requestedGapV) && requestedGapV >= 0
            ? requestedGapV : (s.gapV ?? 3)
        );
        setShowCenter(
          requestedShowCenter === "true" ? true :
          requestedShowCenter === "false" ? false : (s.showCenter ?? false)
        );
        setCenterDotSize(
          params.has("centerDotSize") && Number.isFinite(requestedCenterDotSize) && requestedCenterDotSize > 0
            ? requestedCenterDotSize : (s.centerDotSize ?? 1.5)
        );
        setCenterDotColor(requestedCenterDotColor || s.centerDotColor || "#e22020");
        setDefaultFont(s.defaultFont || { fontFamily: "sans", bold: true, color: "#111111" });
        const nextLayout = resolveLayout(
          hasPreset ? requestedInnerD : (s.innerD ?? 45),
          hasPreset ? requestedOuterD : (s.outerD ?? 55.5),
          nextGridPreset,
          params.has("gapH") && Number.isFinite(requestedGapH) && requestedGapH >= 0
            ? requestedGapH : (s.gapH ?? 0),
          params.has("gapV") && Number.isFinite(requestedGapV) && requestedGapV >= 0
            ? requestedGapV : (s.gapV ?? 3)
        );
        const reflowStartedAt = performance.now();
        const preparedPages = loadedPages.length
          ? reflowPages(loadedPages, nextLayout.CELLS_PER_PAGE)
          : [makeEmptyPage(nextLayout.CELLS_PER_PAGE)];
        console.info("[Load timing] Reflow:", Math.round(performance.now() - reflowStartedAt), "ms",
          "| resulting pages:", preparedPages.length);
        setPages(preparedPages);
        setTimeout(() => { skipAutosave.current = false; }, 300);
      } catch (e) {
        toast.dismiss(loadingToastId);
        toast.error(isEnglish ? "Failed to load project" : "Gagal memuat proyek");
        navigate("/");
      } finally {
        toast.dismiss(loadingToastId);
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      toast.dismiss(loadingToastId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---- build save payload ----
  const buildPayload = (pgs) =>
    pgs.map((pg) => ({
      cells: pg.cells.map((c) => {
        const base = {
          index: c.index, type: c.type, text: c.text, fillColor: c.fillColor || null,
          bgColor: c.bgColor || null,
          zoom: c.zoom, offX: c.offX, offY: c.offY,
          fontFamily: c.fontFamily || "sans", bold: c.bold ?? true, color: c.color || "#111111",
        };
        if (c.type === "image") {
          if (c.imageId) base.imageId = c.imageId;
          else if (c.imageData) base.imageData = c.imageData;
        }
        return base;
      }),
    }));

  const mergeImageIds = (prev, respPages) => {
    let changed = false;
    const next = prev.map((pg, p) => ({
      cells: pg.cells.map((c, i) => {
        const r = respPages?.[p]?.cells?.[i];
        if (c.type === "image" && !c.imageId && r?.imageId) {
          changed = true;
          return { ...c, imageId: r.imageId };
        }
        return c;
      }),
    }));
    return changed ? next : prev;
  };

  const save = useCallback(async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaveStatus("saving");
    try {
      const payload = {
        name: nameRef.current,
        pages: buildPayload(pagesRef.current),
        settings: settingsRef.current,
      };
      const res = await api.put(`/projects/${id}`, payload);
      const merged = mergeImageIds(pagesRef.current, res.data.pages);
      if (merged !== pagesRef.current) {
        internalUpdate.current = true; // this pages change is not a user edit -> don't re-autosave
        applyingHistory.current = true; // ...and must NOT create an undo entry
        pagesRef.current = merged;
        setPages(merged);
      }
      setSaveStatus("saved");
      return true;
    } catch (e) {
      setSaveStatus("error");
      toast.error(isEnglish ? "Failed to save" : "Gagal menyimpan");
      return false;
    }
  }, [id, isEnglish]);

  // ---- autosave (debounced) ----
  const scheduleSave = useCallback(() => {
    if (skipAutosave.current) return;
    setSaveStatus("dirty");
    if (!autoSaveRef.current) return; // auto save OFF: hanya tandai belum disimpan
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { save(); }, 1200);
  }, [save]);

  const persistUserSettings = useCallback(async (patch) => {
    try {
      await api.put("/user/settings", patch);
      return true;
    } catch (e) {
      toast.error(isEnglish ? "Failed to save account settings" : "Pengaturan akun gagal disimpan");
      return false;
    }
  }, [isEnglish]);

  const toggleAutoSave = useCallback(async (on) => {
    setAutoSaveOn(on);
    localStorage.setItem("pinmaker_autosave", on ? "on" : "off");
    await persistUserSettings({ autoSave: on });
    if (!on) {
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      toast.info(isEnglish ? "Auto save is OFF — remember to click Save" : "Auto save NONAKTIF — jangan lupa klik Simpan");
    } else {
      toast.success(isEnglish ? "Auto save is ON" : "Auto save AKTIF");
      if (saveStatusRef.current === "dirty" || saveStatusRef.current === "error") save();
    }
  }, [save, persistUserSettings, isEnglish]);

  useEffect(() => {
    if (loading || skipAutosave.current) return;
    if (internalUpdate.current) { internalUpdate.current = false; return; }
    scheduleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, name, gridPreset, innerD, outerD, gapH, gapV, showCenter, centerDotSize, centerDotColor, defaultFont]);

  // ---- undo/redo history ----
  const snapshot = (pgs) => pgs.map((pg) => ({ cells: pg.cells.slice() }));

  // Record page changes into the undo stack (skips programmatic loads & history restores).
  useEffect(() => {
    if (loading || skipAutosave.current) { lastSnap.current = snapshot(pages); return; }
    if (applyingHistory.current) { applyingHistory.current = false; lastSnap.current = snapshot(pages); return; }
    if (lastSnap.current) {
      undoStack.current.push(lastSnap.current);
      if (undoStack.current.length > 60) undoStack.current.shift();
      redoStack.current = [];
      setCanUndo(true);
      setCanRedo(false);
    }
    lastSnap.current = snapshot(pages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, loading]);

  const undo = useCallback(() => {
    if (!undoStack.current.length) return;
    const prev = undoStack.current.pop();
    redoStack.current.push(snapshot(pagesRef.current));
    applyingHistory.current = true;
    lastSnap.current = prev;
    pagesRef.current = prev;
    setPages(prev);
    setSelected(null);
    setCurrentPage((c) => Math.min(c, prev.length - 1));
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    if (!redoStack.current.length) return;
    const next = redoStack.current.pop();
    undoStack.current.push(snapshot(pagesRef.current));
    applyingHistory.current = true;
    lastSnap.current = next;
    pagesRef.current = next;
    setPages(next);
    setSelected(null);
    setCurrentPage((c) => Math.min(c, next.length - 1));
    setCanRedo(redoStack.current.length > 0);
    setCanUndo(true);
  }, []);

  // keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl+Y (redo)
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // ---- cell mutations ----
  const updateCell = (pageIdx, cellIdx, patch) => {
    setPages((prev) => {
      // Only clone the affected page so unchanged pages keep their reference
      // and their (memoized) thumbnails don't re-render on every drag/zoom step.
      const next = prev.slice();
      const cells = next[pageIdx].cells.slice();
      cells[cellIdx] = { ...cells[cellIdx], ...patch };
      next[pageIdx] = { cells };
      return next;
    });
  };

  const onChangeTransform = (pageIdx, cellIdx, patch) => updateCell(pageIdx, cellIdx, patch);

  // Toggle left pages rail (persisted)
  const toggleLeftRail = () => {
    setShowLeftRail((v) => {
      const next = !v;
      localStorage.setItem("pinmaker_leftrail", next ? "on" : "off");
      return next;
    });
  };

  // Scroll past the top/bottom edge of the canvas -> previous/next page
  const onCanvasWheel = (e) => {
    const el = canvasScrollRef.current;
    if (!el) return;
    const now = Date.now();
    if (now - wheelCooldown.current < 550) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    const atTop = el.scrollTop <= 2;
    if (e.deltaY > 30 && atBottom && currentPage < pages.length - 1) {
      wheelCooldown.current = now;
      setCurrentPage((c) => Math.min(c + 1, pages.length - 1));
      requestAnimationFrame(() => { el.scrollTop = 0; });
    } else if (e.deltaY < -30 && atTop && currentPage > 0) {
      wheelCooldown.current = now;
      setCurrentPage((c) => Math.max(c - 1, 0));
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
    }
  };

  const handleUploadToCell = async (file) => {
    if (!selected) return;
    setBusy(true);
    try {
      const dataURL = await processImageFile(file);
      updateCell(selected.pageIdx, selected.cellIdx, {
        type: "image", imageData: dataURL, imageId: null, text: null,
        zoom: 1, offX: 0, offY: 0,
      });
    } finally { setBusy(false); }
  };

  const handleSetText = (payload) => {
    if (!selected) return;
    const { text, fontFamily = "sans", bold = true, color = "#111111" } =
      typeof payload === "string" ? { text: payload } : (payload || {});
    if (!text) return;
    updateCell(selected.pageIdx, selected.cellIdx, {
      type: "text", text, fontFamily, bold, color,
      imageData: null, imageId: null, bgColor: null, zoom: 1, offX: 0, offY: 0,
    });
  };

  const handleDelete = () => {
    if (!selected) return;
    updateCell(selected.pageIdx, selected.cellIdx, {
      type: "empty", imageData: null, imageId: null, text: null, fillColor: null, bgColor: null, zoom: 1, offX: 0, offY: 0,
    });
  };

  const handleSetColor = (fillColor) => {
    if (!selected || !fillColor) return;
    updateCell(selected.pageIdx, selected.cellIdx, {
      type: "color", fillColor, imageData: null, imageId: null, text: null, zoom: 1, offX: 0, offY: 0,
    });
  };

  const handleReset = () => {
    if (!selected) return;
    updateCell(selected.pageIdx, selected.cellIdx, { zoom: 1, offX: 0, offY: 0 });
  };

  const handleApplyCrop = (dataURL) => {
    if (!selected || !dataURL) { setCropOpen(false); return; }
    updateCell(selected.pageIdx, selected.cellIdx, {
      imageData: dataURL, imageId: null, zoom: 1, offX: 0, offY: 0,
    });
    setCropOpen(false);
    toast.success(isEnglish ? "Image cropped" : "Gambar dipangkas");
  };

  // ---- duplicate the selected image N times, inserting the copies right AFTER
  // the source cell so any following content (e.g. the order-number text) shifts
  // down and stays behind the images. ----
  const handleDuplicateImage = (count) => {
    const sel = selected;
    const src = sel ? pagesRef.current[sel.pageIdx]?.cells[sel.cellIdx] : null;
    if (!src || src.type !== "image" || !(src.imageData || src.imageId)) {
      toast.error(isEnglish ? "Please select an image first" : "Pilih sebuah gambar dulu");
      return;
    }
    const n = Math.max(1, parseInt(count, 10) || 0);
    if (!n) return;

    // 1) Flatten every cell across all pages into one linear sequence.
    const flat = [];
    pagesRef.current.forEach((pg) => pg.cells.forEach((c) => flat.push(c)));
    const srcFlatIdx = sel.pageIdx * 15 + sel.cellIdx;

    const makeCopy = () => ({
      type: "image",
      imageData: src.imageData || null,
      imageId: src.imageId || null,
      text: null, fillColor: null,
      bgColor: src.bgColor || null,
      zoom: src.zoom || 1, offX: src.offX || 0, offY: src.offY || 0,
    });
    const copies = Array.from({ length: n }, makeCopy);

    // 2) Insert the copies immediately after the source cell (push the rest down).
    let newFlat = [
      ...flat.slice(0, srcFlatIdx + 1),
      ...copies,
      ...flat.slice(srcFlatIdx + 1),
    ];

    // 3) Drop trailing empty cells so we don't create unnecessary blank pages.
    let lastFilled = -1;
    newFlat.forEach((c, i) => { if (c && c.type !== "empty") lastFilled = i; });
    newFlat = newFlat.slice(0, lastFilled + 1);

    // 4) Re-chunk into pages of 15, padding the last page with empty cells.
    const perPage = layout.CELLS_PER_PAGE;
    const pageCount = Math.max(1, Math.ceil(newFlat.length / perPage));
    const working = [];
    for (let p = 0; p < pageCount; p++) {
      const cells = [];
      for (let i = 0; i < perPage; i++) {
        const c = newFlat[p * perPage + i];
        cells.push(c ? { ...c, index: i } : emptyCell(i));
      }
      working.push({ cells });
    }

    setPages(working);
    pagesRef.current = working;
    // jump to the page where the last inserted copy landed
    setCurrentPage(Math.floor((srcFlatIdx + n) / layout.CELLS_PER_PAGE));
    setSelected(null);
    setDupOpen(false);
    toast.success(`${n} salinan gambar ditempatkan`);
  };

  // ---- multi-image upload with auto fill + auto pages ----
  const handleMultiUpload = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setBusy(true);
    const t = toast.loading(`Memproses ${files.length} gambar…`);
    try {
      let working = pagesRef.current.map((pg) => ({ cells: pg.cells.slice() }));
      let lastPage = currentPage;
      for (const f of files) {
        const dataURL = await processImageFile(f);
        let spot = findNextEmpty(working);
        if (!spot) {
          working = [...working, makeEmptyPage(layout.CELLS_PER_PAGE)];
          spot = { pageIdx: working.length - 1, cellIdx: 0 };
        }
        working[spot.pageIdx].cells[spot.cellIdx] = {
          index: spot.cellIdx, type: "image", imageData: dataURL, imageId: null,
          text: null, zoom: 1, offX: 0, offY: 0,
        };
        lastPage = spot.pageIdx;
      }
      setPages(working);
      setCurrentPage(lastPage);
      pagesRef.current = working;
      // Tampilkan notifikasi berhasil terlebih dahulu.
      toast.success(`${files.length} gambar ditempatkan`, {
        id: t,
        duration: 600,
      });

      // Tutup toast terlebih dahulu, lalu buka modal nomor pesanan.
      if (orderPromptEnabled) {
        setLastBatchCount(files.length);
        setOrderNumber("");
        window.setTimeout(() => {
          toast.dismiss(t);
          setOrderPromptOpen(true);
        }, 2000);
      }
    } catch (e) {
      toast.error(isEnglish ? "Failed to process images" : "Gagal memproses gambar", { id: t });
    } finally {
      setBusy(false);
    }
  };


  // Pilih folder induk, lalu kelompokkan gambar berdasarkan subfolder langsung.

  const handleFolderSelection = (inputEntries) => {
    const entries = Array.from(inputEntries || []).map((item) => {
      if (item && item.file && item.folderName) {
        return { file: item.file, folderName: item.folderName };
      }

      const file = item;
      const parts = String(file?.webkitRelativePath || "")
        .split("/")
        .filter(Boolean);

      return {
        file,
        folderName: parts.length >= 3 ? parts[1] : "",
      };
    }).filter(({ file, folderName }) =>
      file?.type?.startsWith("image/") && folderName
    );

    const grouped = new Map();

    for (const { file, folderName } of entries) {
      if (!grouped.has(folderName)) grouped.set(folderName, []);
      grouped.get(folderName).push(file);
    }

    const groups = Array.from(grouped.entries())
      .map(([folderName, files]) => {
        const match = folderName.trim().match(/([A-Za-z0-9]{6})[^A-Za-z0-9]*$/);
        return {
          folderName,
          orderNumber: match ? match[1] : "",
          files: files.sort((a, b) =>
            (a.webkitRelativePath || a.name).localeCompare(
              b.webkitRelativePath || b.name,
              undefined,
              { numeric: true, sensitivity: "base" }
            )
          ),
        };
      })
      .sort((a, b) =>
        a.folderName.localeCompare(
          b.folderName,
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      );

    if (!groups.length) {
      toast.error(isEnglish
        ? "No customer subfolders containing images were found."
        : "Tidak ditemukan subfolder pelanggan yang berisi gambar.");
      return;
    }

    setFolderUploadInfoMode("order");
    setFolderUploadGroups(groups);
    setFolderUploadDialogOpen(true);
  };

  const handlePickFolder = async () => {
    if (proFeatureEnabled !== true) {
      toast.info(
        proFeatureEnabled === false
          ? (isEnglish
              ? "This feature is not active for your account. Contact the admin to request PRO activation."
              : "Fitur ini belum aktif untuk akun Anda. Hubungi admin untuk meminta aktivasi fitur PRO.")
          : (isEnglish
              ? "PRO access is being checked. Please try again."
              : "Akses PRO sedang diperiksa. Silakan coba kembali.")
      );
      return;
    }

    if (typeof window.showDirectoryPicker !== "function") {
      folderInputRef.current?.click();
      return;
    }

    try {
      const rootHandle = await window.showDirectoryPicker({
        id: "pin-maker-multiple-folder-upload",
        mode: "read",
      });

      const selectedEntries = [];

      const collectImages = async (directoryHandle, folderName) => {
        for await (const [entryName, entryHandle] of directoryHandle.entries()) {
          if (entryHandle.kind === "file") {
            const file = await entryHandle.getFile();
            if (file.type && file.type.startsWith("image/")) {
              selectedEntries.push({ file, folderName });
            }
          } else if (entryHandle.kind === "directory") {
            await collectImages(entryHandle, folderName);
          }
        }
      };

      for await (const [folderName, folderHandle] of rootHandle.entries()) {
        if (folderHandle.kind === "directory") {
          await collectImages(folderHandle, folderName);
        }
      }

      handleFolderSelection(selectedEntries);
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.error("Gagal memilih folder:", error);
      toast.error(isEnglish
        ? "Could not read the selected folder."
        : "Folder tidak dapat dibaca. Silakan coba kembali.");
    }
  };

  // Masukkan foto setiap folder, lalu satu slot teks nomor pesanan enam digit.
  const confirmFolderUpload = async () => {
    let accessResponse;

    try {
      accessResponse = await api.get("/user/feature-access");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        (isEnglish
          ? "Could not verify PRO access."
          : "Tidak dapat memeriksa akses PRO.")
      );
      return;
    }

    if (!accessResponse.data?.pro_feature_enabled) {
      setProFeatureEnabled(false);
      setFolderUploadDialogOpen(false);
      toast.info(
        isEnglish
          ? "This feature is not active for your account. Contact the admin to request PRO activation."
          : "Fitur ini belum aktif untuk akun Anda. Hubungi admin untuk meminta aktivasi fitur PRO."
      );
      return;
    }

    setProFeatureEnabled(true);
    const groups = folderUploadGroups;

    const needsOrderNumber = folderUploadInfoMode === "order";

    if (!groups.length || (needsOrderNumber && groups.some((group) => !group.orderNumber))) {
      toast.error(isEnglish
        ? "Folder names must end with six letters or numbers."
        : "Enam karakter terakhir nama setiap folder harus berupa huruf atau angka.");
      return;
    }

    const totalImages = groups.reduce(
      (total, group) => total + group.files.length,
      0
    );

    setBusy(true);
    const loadingId = toast.loading(
      isEnglish
        ? `Processing ${totalImages} images from ${groups.length} folders…`
        : `Memproses ${totalImages} gambar dari ${groups.length} folder…`
    );

    try {
      let working = pagesRef.current.map((page) => ({
        cells: page.cells.slice(),
      }));
      let lastPage = currentPage;

      for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
        const group = groups[groupIndex];

        // Foto dalam folder yang sama tetap ditempatkan berurutan.
        for (const file of group.files) {
          const dataURL = await processImageFile(file);
          let spot = findNextEmpty(working);

          if (!spot) {
            working = [
              ...working,
              makeEmptyPage(layout.CELLS_PER_PAGE),
            ];
            spot = {
              pageIdx: working.length - 1,
              cellIdx: 0,
            };
          }

          working[spot.pageIdx].cells[spot.cellIdx] = {
            index: spot.cellIdx,
            type: "image",
            imageData: dataURL,
            imageId: null,
            text: null,
            zoom: 1,
            offX: 0,
            offY: 0,
          };

          lastPage = spot.pageIdx;
        }

        let infoText = "";

        if (folderUploadInfoMode === "order") {
          infoText = group.orderNumber;
        } else if (folderUploadInfoMode === "count") {
          infoText = `${group.files.length} PCS`;
        } else if (folderUploadInfoMode === "sequence") {
          infoText = String(groupIndex + 1).padStart(5, "0");
        }

        // Tanpa Informasi berarti tidak membuat sel teks.
        if (infoText) {
          let infoSpot = findNextEmpty(working);

          if (!infoSpot) {
            working = [
              ...working,
              makeEmptyPage(layout.CELLS_PER_PAGE),
            ];
            infoSpot = {
              pageIdx: working.length - 1,
              cellIdx: 0,
            };
          }

          const font = settingsRef.current.defaultFont;

          working[infoSpot.pageIdx].cells[infoSpot.cellIdx] = {
            index: infoSpot.cellIdx,
            type: "text",
            text: infoText,
            imageData: null,
            imageId: null,
            zoom: 1,
            offX: 0,
            offY: 0,
            fontFamily: font.fontFamily,
            bold: font.bold,
            color: font.color,
          };

          lastPage = infoSpot.pageIdx;
        }
      }

      setPages(working);
      pagesRef.current = working;
      setCurrentPage(lastPage);
      setSelected(null);

      setFolderUploadDialogOpen(false);
      setFolderUploadGroups([]);

      toast.success(
        folderUploadInfoMode === "none"
          ? (isEnglish
              ? `${totalImages} images from ${groups.length} folders placed`
              : `${totalImages} gambar dari ${groups.length} folder berhasil ditempatkan`)
          : (isEnglish
              ? `${totalImages} images and ${groups.length} information labels placed`
              : `${totalImages} gambar dan ${groups.length} label informasi berhasil ditempatkan`),
        { id: loadingId }
      );

      // Tampilkan peringatan setelah preview ditutup.
      setTimeout(() => setFolderUploadNoticeOpen(true), 400);
    } catch (error) {
      console.error("Upload banyak folder gagal:", error);
      toast.error(
        isEnglish
          ? "Failed to process folders. Please try again."
          : "Gagal memproses folder. Silakan coba kembali.",
        { id: loadingId }
      );
    } finally {
      setBusy(false);
    }
  };

  // Place the order-number text into the next empty position (after the batch).
  const confirmOrderNumber = () => {
    const val = orderNumber.trim();
    if (!val) { setOrderPromptOpen(false); return; }
    let working = pagesRef.current.map((pg) => ({ cells: pg.cells.slice() }));
    let spot = findNextEmpty(working);
    if (!spot) {
      working = [...working, makeEmptyPage(layout.CELLS_PER_PAGE)];
      spot = { pageIdx: working.length - 1, cellIdx: 0 };
    }
    working[spot.pageIdx].cells[spot.cellIdx] = {
      index: spot.cellIdx, type: "text", text: val, imageData: null, imageId: null,
      zoom: 1, offX: 0, offY: 0,
      fontFamily: settingsRef.current.defaultFont.fontFamily,
      bold: settingsRef.current.defaultFont.bold,
      color: settingsRef.current.defaultFont.color,
    };
    setPages(working);
    pagesRef.current = working;
    setCurrentPage(spot.pageIdx);
    setSelected({ pageIdx: spot.pageIdx, cellIdx: spot.cellIdx });
    setOrderPromptOpen(false);
    toast.success(isEnglish ? `Order number "${val}" added` : `Nomor pesanan "${val}" ditambahkan`);
  };

  const skipOrderNumber = () => {
    setOrderPromptOpen(false);
    setOrderNumber("");
  };

  const addPage = () => {
    setPages((prev) => [...prev, makeEmptyPage(layout.CELLS_PER_PAGE)]);
    setCurrentPage(pages.length);
    toast.success(isEnglish ? "New page added" : "Halaman baru ditambahkan", { className: "pms-toast-page-aligned" });
  };

  const deletePage = (idx) => {
    if (pages.length <= 1) { toast.error(isEnglish ? "At least one page is required" : "Minimal harus ada 1 halaman"); return; }
    setPages((prev) => prev.filter((_, i) => i !== idx));
    setCurrentPage((cp) => Math.max(0, cp >= idx ? cp - 1 : cp));
    setSelected(null);
    setDeletePageIdx(null);
    toast.success(`Halaman ${idx + 1} dihapus`);
  };

  // ---- auto-fill: duplicate existing images into empty cells so the page is full ----
  // Sources priority: a page's OWN images first; if the page is empty, fall back to
  // the GLOBAL pool of every image across all pages (so blank pages get filled too).
  const runFillImages = () => {
    const targetIdxs = Array.from(fillPageSet).sort((a, b) => a - b);
    if (!targetIdxs.length) { toast.error(isEnglish ? "Select at least one page" : "Pilih minimal satu halaman"); return; }
    if (!fillAllPositions && fillPositionSet.size === 0) {
      toast.error(isEnglish ? "Select at least one circle/position" : "Pilih minimal satu lingkaran/posisi");
      return;
    }
    const working = pagesRef.current.map((pg) => ({ cells: pg.cells.map((c) => ({ ...c })) }));

    // Global pool: every image cell across ALL pages (page-major, column-major).
    const globalPool = [];
    working.forEach((pg) =>
      pg.cells.forEach((c) => {
        if (c.type === "image" && (c.imageData || c.imageId)) globalPool.push(c);
      })
    );
    // The image at the LAST position (last-numbered) across the whole project.
    const lastImage = globalPool.length ? globalPool[globalPool.length - 1] : null;

    let filledTotal = 0;
    const noSourcePages = [];
    let hasTextLeft = false;

    targetIdxs.forEach((pi) => {
      const page = working[pi];
      if (!page) return;
      let sources;
      if (fillSource === "last") {
        // use ONLY the single last image everywhere
        sources = lastImage ? [lastImage] : [];
      } else {
        // all images: prefer the page's own images, else the global pool
        const localSources = page.cells.filter(
          (c) => c.type === "image" && (c.imageData || c.imageId)
        );
        sources = localSources.length ? localSources : globalPool;
      }
      if (sources.length === 0) { noSourcePages.push(pi + 1); return; }
      let si = 0;
      page.cells = page.cells.map((c, ci) => {
        const positionAllowed = fillAllPositions || fillPositionSet.has(ci);
        if (c.type === "empty" && positionAllowed) {
          const src = sources[si % sources.length];
          si += 1;
          filledTotal += 1;
          return {
            ...c,
            index: ci,
            type: "image",
            imageData: src.imageData || null,
            imageId: src.imageId || null,
            text: null,
            bgColor: src.bgColor || null,
            zoom: src.zoom ?? 1,
            offX: src.offX ?? 0,
            offY: src.offY ?? 0,
          };
        }
        if (c.type === "text") hasTextLeft = true;
        return c;
      });
    });

    setPages(working);
    pagesRef.current = working;
    setSelected(null);
    setFillDialogOpen(false);

    if (filledTotal > 0) {
      toast.success(`${filledTotal} sel kosong diisi dengan gambar`);
    } else if (noSourcePages.length === 0) {
      toast.info(isEnglish ? "There are no empty cells to fill" : "Tidak ada sel kosong untuk diisi");
    }
    if (noSourcePages.length) {
      toast.warning(
        `Halaman ${noSourcePages.join(", ")} tidak bisa diisi — belum ada gambar sama sekali di proyek`
      );
    }
    if (hasTextLeft) {
      toast.warning("Beberapa sel berisi teks dan dibiarkan apa adanya");
    }
  };

  const openFillDialog = () => {
    // preselect pages that still have empty cells
    const withEmpty = pagesRef.current
      .map((pg, i) => ({ i, hasEmpty: pg.cells.some((c) => c.type === "empty") }))
      .filter((x) => x.hasEmpty)
      .map((x) => x.i);
    setFillPageSet(new Set(withEmpty.length ? withEmpty : [currentPage]));
    setFillDialogOpen(true);
  };

  const toggleFillPage = (idx) => {
    setFillPageSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleFillAll = () => {
    setFillPageSet((prev) =>
      prev.size === pagesRef.current.length ? new Set() : new Set(pagesRef.current.map((_, i) => i))
    );
  };

  // ---- clear: empty ALL cells (images & text) on selected pages ----
  const runClearPages = () => {
    const targetIdxs = Array.from(clearPageSet).sort((a, b) => a - b);
    if (!targetIdxs.length) { toast.error(isEnglish ? "Select at least one page" : "Pilih minimal satu halaman"); return; }
    const targetSet = new Set(targetIdxs);
    const working = pagesRef.current.map((pg, pi) =>
      targetSet.has(pi) ? makeEmptyPage(layout.CELLS_PER_PAGE) : { cells: pg.cells.map((c) => ({ ...c })) }
    );
    setPages(working);
    pagesRef.current = working;
    setSelected(null);
    setClearDialogOpen(false);
    toast.success(
      targetIdxs.length === 1
        ? `Halaman ${targetIdxs[0] + 1} dikosongkan`
        : `${targetIdxs.length} halaman dikosongkan`
    );
  };

  const openClearDialog = () => {
    setClearPageSet(new Set([currentPage]));
    setClearDialogOpen(true);
  };

  const toggleClearPage = (idx) => {
    setClearPageSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleClearAll = () => {
    setClearPageSet((prev) =>
      prev.size === pagesRef.current.length ? new Set() : new Set(pagesRef.current.map((_, i) => i))
    );
  };

  // ---- bulk delete: remove selected pages entirely (keep at least 1) ----
  const runDeletePages = () => {
    const idxs = Array.from(deletePagesSet).sort((a, b) => a - b);
    if (!idxs.length) { toast.error(isEnglish ? "Select at least one page" : "Pilih minimal satu halaman"); return; }
    if (idxs.length >= pagesRef.current.length) {
      toast.error(isEnglish ? "At least one page must remain" : "Minimal harus ada 1 halaman tersisa");
      return;
    }
    const delSet = new Set(idxs);
    const working = pagesRef.current.filter((_, i) => !delSet.has(i));
    setPages(working);
    pagesRef.current = working;
    setCurrentPage((cp) => Math.max(0, Math.min(cp, working.length - 1)));
    setSelected(null);
    setDeletePagesDialogOpen(false);
    toast.success(
      idxs.length === 1
        ? `Halaman ${idxs[0] + 1} dihapus`
        : `${idxs.length} halaman dihapus`
    );
  };

  const openDeletePagesDialog = () => {
    if (pages.length <= 1) { toast.error(isEnglish ? "At least one page is required" : "Minimal harus ada 1 halaman"); return; }
    setDeletePagesSet(new Set());
    setDeletePagesDialogOpen(true);
  };

  const toggleDeletePage = (idx) => {
    setDeletePagesSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Set global default text style AND apply it to ALL existing text cells.
  const applyDefaultFont = (patch) => {
    const next = { ...settingsRef.current.defaultFont, ...patch };
    setDefaultFont(next);
    settingsRef.current = { ...settingsRef.current, defaultFont: next };
    const updatedPages = pagesRef.current.map((pg) => ({
      cells: pg.cells.map((c) =>
        c.type === "text"
          ? { ...c, fontFamily: next.fontFamily, bold: next.bold, color: next.color }
          : c
      ),
    }));
    // Update ref immediately to avoid race condition with save
    pagesRef.current = updatedPages;
    setPages(updatedPages);
  };

  // ---- Load original image data only when needed for PDF / Print ----
  const preparePagesForOutput = async (selectedPages) => {
    const startedAt = performance.now();

    const outputPages = selectedPages.map((pg) => ({
      ...pg,
      cells: pg.cells.slice(),
    }));

    // Satu imageId hanya diunduh sekali meskipun dipakai beberapa sel.
    const positionsByImage = new Map();

    outputPages.forEach((pg, pageIndex) => {
      pg.cells.forEach((cell, cellIndex) => {
        if (cell.type !== "image" || cell.imageData || !cell.imageId) return;

        const imageId = cell.imageId;
        if (!positionsByImage.has(imageId)) {
          positionsByImage.set(imageId, []);
        }
        positionsByImage.get(imageId).push({ pageIndex, cellIndex });
      });
    });

    const imageIds = Array.from(positionsByImage.keys());
    let cursor = 0;

    const worker = async () => {
      while (cursor < imageIds.length) {
        const imageId = imageIds[cursor++];
        const imageData = await getProjectImageDataUrl(id, imageId);

        for (const position of positionsByImage.get(imageId)) {
          const cell = outputPages[position.pageIndex].cells[position.cellIndex];
          outputPages[position.pageIndex].cells[position.cellIndex] = {
            ...cell,
            imageData,
          };
        }
      }
    };

    // Maksimum 4 unduhan gambar asli berjalan bersamaan.
    const workerCount = Math.min(4, imageIds.length);
    const results = await Promise.all(
      Array.from({ length: workerCount }, async () => {
        try {
          await worker();
          return null;
        } catch (error) {
          return error;
        }
      })
    );

    const failure = results.find(Boolean);
    if (failure) throw failure;

    console.info(
      "[Output timing] Original images:",
      Math.round(performance.now() - startedAt),
      "ms | unique images:",
      imageIds.length
    );

    return outputPages;
  };

  // ---- export / print with page selection ----
  const runExport = async (selPages) => {
    setBusy(true);
    const t = toast.loading("Memuat gambar dan membuat PDF…");
    try {
      const preparedPages = await preparePagesForOutput(selPages);
      await exportPDF(preparedPages, `${nameRef.current || "pin-sheet"}.pdf`, { gridPreset, innerD, outerD, gapH, gapV, showCenter, centerDotSize, centerDotColor });

      // Catat trafik hanya setelah PDF berhasil dibuat.
      // Kegagalan pencatatan tidak boleh menggagalkan hasil ekspor.
      try {
        await api.post("/usage/pin-export", {
          pages_count: preparedPages.length,
        });
      } catch (trackingError) {
        console.warn("[PIN Traffic] Gagal mencatat ekspor:", trackingError);
      }

      toast.success(isEnglish ? "PDF downloaded" : "PDF diunduh", { id: t });
    } catch (e) {
      console.error("[PDF Export] Gagal:", e);
      toast.error(isEnglish ? "Failed to create PDF" : "Gagal membuat PDF", { id: t });
    } finally { setBusy(false); }
  };

  const runPrint = async (selPages) => {
    setBusy(true);
    const t = toast.loading("Memuat gambar untuk cetak…");
    try {
      const preparedPages = await preparePagesForOutput(selPages);
      await printPDF(
        preparedPages,
        { gridPreset, innerD, outerD, gapH, gapV, showCenter, centerDotSize, centerDotColor }
      );

      // Catat Print setelah PDF berhasil disiapkan dan jendela cetak dibuka.
      // Kegagalan pencatatan tidak boleh menggagalkan proses Print.
      try {
        await api.post("/usage/pin-export", {
          pages_count: preparedPages.length,
          activity_type: "print",
        });
      } catch (trackingError) {
        console.warn("[PIN Traffic] Gagal mencatat Print:", trackingError);
      }

      toast.dismiss(t);
    } catch (e) {
      console.error("[PDF Print] Gagal:", e);
      toast.error(isEnglish ? "Failed to prepare printing" : "Gagal menyiapkan cetak", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const openOutputDialog = (mode) => {
    setOutputMode(mode);
    // default: all pages selected
    setSelectedPageSet(new Set(pagesRef.current.map((_, i) => i)));
    setOutputDialogOpen(true);
  };

  const togglePageSel = (idx) => {
    setSelectedPageSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleSelectAllPages = () => {
    setSelectedPageSet((prev) => {
      if (prev.size === pagesRef.current.length) return new Set();
      return new Set(pagesRef.current.map((_, i) => i));
    });
  };

  const confirmOutput = async () => {
    const idxs = Array.from(selectedPageSet).sort((a, b) => a - b);
    if (!idxs.length) {
      toast.error(isEnglish ? "Select at least one page" : "Pilih minimal satu halaman");
      return;
    }

    const selPages = idxs.map((i) => pagesRef.current[i]).filter(Boolean);
    setOutputDialogOpen(false);

    if (outputMode === "print") {
      await runPrint(selPages);
    } else {
      await runExport(selPages);
    }
  };
  const doExport = () => openOutputDialog("export");
  const doPrint = () => openOutputDialog("print");


  const nextEmpty = useMemo(() => findNextEmpty(pages), [pages]);
  const selectedCell = selected ? pages[selected.pageIdx]?.cells[selected.cellIdx] : null;

  useEffect(() => {
    let cancelled = false;

    if (!cropOpen || selectedCell?.type !== "image") {
      setCropSrc(null);
      return;
    }

    if (selectedCell.imageData) {
      setCropSrc(selectedCell.imageData);
      return;
    }

    setCropSrc(null);

    if (selectedCell.imageId) {
      getProjectImageDataUrl(id, selectedCell.imageId)
        .then((dataURL) => {
          if (!cancelled) setCropSrc(dataURL);
        })
        .catch((error) => {
          console.error("[Crop] Gagal memuat gambar:", error);
          if (!cancelled) toast.error("Gagal memuat gambar untuk crop");
        });
    }

    return () => {
      cancelled = true;
    };
  }, [cropOpen, selectedCell?.type, selectedCell?.imageData, selectedCell?.imageId, id]);

  const stats = useMemo(() => {
    let filled = 0;
    pages.forEach((pg) => pg.cells.forEach((c) => { if (c.type !== "empty") filled++; }));
    return { filled, capacity: pages.length * layout.CELLS_PER_PAGE };
  }, [pages, layout.CELLS_PER_PAGE]);

  // drag & drop on canvas area
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleMultiUpload(e.dataTransfer.files);
  };

  if (loading) return null;

  const SaveBadge = () => {
    const map = {
      saved: { icon: <Check className="h-3.5 w-3.5" />, text: isEnglish ? "Saved" : "Tersimpan", cls: "text-blue-100" },
      saving: { icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />, text: "Menyimpan…", cls: "text-blue-100" },
      dirty: { icon: <Loader2 className="h-3.5 w-3.5" />, text: isEnglish ? "Unsaved" : "Belum disimpan", cls: "text-[hsl(var(--warn))]" },
      error: { icon: <CloudOff className="h-3.5 w-3.5" />, text: "Gagal simpan", cls: "text-destructive" },
    };
    const s = map[saveStatus] || map.saved;
    return (
      <span data-testid="editor-save-status" className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-1 text-xs border-blue-800 bg-[#1b2b4b] text-blue-100 ${s.cls}`}>
        {s.icon} {s.text}
      </span>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-screen flex-col bg-background">
        {/* Toolbar */}
        <header className={`sticky top-0 z-30 flex items-center gap-3 border-b px-4 py-2 shadow-md backdrop-blur border-blue-900 bg-[#101d3a] text-white shadow-blue-950/20`}>
          <Button variant="ghost" size="icon" className={`rounded-xl border transition-colors border-blue-800 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white`} data-testid="editor-back-button"
            onClick={() => {
              if (saveStatusRef.current === "dirty" || saveStatusRef.current === "error") setLeaveDialogOpen(true);
              else navigate("/");
            }}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Input
            value={name}
            data-testid="editor-project-name-input"
            onChange={(e) => setName(e.target.value)}
            className={`h-9 w-48 rounded-xl border px-3 font-display font-semibold sm:w-64 border-blue-800 bg-[#1b2b4b] text-blue-100 placeholder:text-blue-200/65 hover:border-cyan-400 hover:bg-[#263b60] focus:border-cyan-400 focus:bg-[#263b60]`}
          />
          <SaveBadge />
          <ExpiryBadge className={`hidden rounded-xl border px-3 py-1 shadow-sm lg:inline-flex border-blue-800 bg-[#1b2b4b] text-blue-100`} />
          {/* Text search across pages */}
          <div className="relative hidden md:block">
            <Search className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-blue-200/70`} />
            <Input
              value={searchQ}
              data-testid="editor-search-input"
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={isEnglish ? "Search text…" : "Cari teks…"}
              className={`h-9 w-40 rounded-xl border pl-8 text-sm md:w-40 border-blue-800 bg-[#1b2b4b] text-blue-100 placeholder:text-blue-200/65 hover:border-cyan-400 hover:bg-[#263b60] focus:border-cyan-400 focus:bg-[#263b60]`}
            />
            {searchQ.trim() && (
              <div data-testid="editor-search-results"
                className="absolute left-0 top-full z-40 mt-1 max-h-72 w-64 overflow-y-auto rounded-xl border bg-card p-1 shadow-lg">
                {searchResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-muted-foreground">Tidak ditemukan</p>
                ) : searchResults.map((r, idx) => (
                  <button
                    key={idx}
                    type="button"
                    data-testid={`editor-search-result-${idx}`}
                    onClick={() => {
                      setCurrentPage(r.pageIdx);
                      setSelected({ pageIdx: r.pageIdx, cellIdx: r.cellIdx });
                      setSearchQ("");
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-secondary"
                  >
                    <span className="truncate font-medium">{r.text}</span>
                    <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
                      Hal {r.pageIdx + 1} · No {r.cellIdx + 1}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <input
              ref={folderInputRef}
              type="file"
              accept="image/*"
              multiple
              webkitdirectory=""
              directory=""
              className="hidden"
              data-testid="editor-upload-folders-input"
              onChange={(e) => {
                const files = e.target.files;
                e.target.value = "";

                if (proFeatureEnabled !== true) {
                  toast.info(
                    proFeatureEnabled === false
                      ? (isEnglish
                          ? "This feature is not active for your account. Contact the admin to request PRO activation."
                          : "Fitur ini belum aktif untuk akun Anda. Hubungi admin untuk meminta aktivasi fitur PRO.")
                      : (isEnglish
                          ? "PRO access is being checked. Please try again."
                          : "Akses PRO sedang diperiksa. Silakan coba kembali.")
                  );
                  return;
                }

                handleFolderSelection(files);
              }}
            />
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
              data-testid="editor-upload-file-input"
              onChange={(e) => { handleMultiUpload(e.target.files); e.target.value = ""; }} />
            <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:bg-[#263b60] hover:text-white`} data-testid="editor-upload-button"
              disabled={busy} onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" /> {isEnglish ? "Upload" : "Unggah"}
            </Button>
            <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:bg-[#263b60] hover:text-white`} data-testid="editor-add-page-button"
              onClick={addPage}>
              <FilePlus2 className="mr-2 h-4 w-4" /> {isEnglish ? "Pages" : "Halaman"}
            </Button>
            <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:bg-[#263b60] hover:text-white`} data-testid="editor-size-button"
              onClick={() => setSizeDialogOpen(true)}>
              <Ruler className="mr-2 h-4 w-4" /> {isEnglish ? "Size" : "Ukuran"}
            </Button>
            <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:bg-[#263b60] hover:text-white`} data-testid="editor-font-button"
              onClick={() => setFontDialogOpen(true)}>
              <Type className="mr-2 h-4 w-4" /> {isEnglish ? "Text" : "Teks"}
            </Button>
            <div className="relative">
              <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:bg-[#263b60] hover:text-white`}
                data-testid="editor-more-button"
                onClick={() => { setMoreMenuOpen(v => !v); setSettingsMenuOpen(false); setNotificationPanelOpen(false); }}>
                <MoreHorizontal className="mr-2 h-4 w-4" /> {isEnglish ? "More" : "Lainnya"}
              </Button>
              {moreMenuOpen && (
                <div className={`absolute right-0 top-full z-50 mt-4 w-56 rounded-xl border p-2 shadow-xl ${
                  editorTheme === "dark"
                    ? "border-[#29466f] bg-[#07132e] text-slate-100"
                    : "border-blue-200 bg-[#f0f6ff] text-slate-900"
                }`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`mb-1 w-full justify-start ${
                      editorTheme === "dark"
                        ? "text-white hover:bg-[#263b60] hover:text-white"
                        : "text-slate-800 hover:bg-blue-100 hover:text-slate-950"
                    }`}
                    data-testid="editor-upload-folders-button"
                    disabled={busy}
                    onClick={() => {
                      setMoreMenuOpen(false);
                      handlePickFolder();
                    }}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {isEnglish ? "Upload Multiple Folders" : "Upload Banyak Folder"}
                    <span className="ml-auto mr-2 shrink-0 text-[9px] font-extrabold leading-none tracking-wide text-emerald-600 dark:text-emerald-400">
                      PRO
                    </span>
                  </Button>
                  <Button variant="ghost" size="sm" className={`mb-1 w-full justify-start ${
                      editorTheme === "dark"
                        ? "text-white hover:bg-[#263b60] hover:text-white"
                        : "text-slate-800 hover:bg-blue-100 hover:text-slate-950"
                    }`} data-testid="editor-clear-button" onClick={() => { setMoreMenuOpen(false); openClearDialog(); }}>
                    <Eraser className="mr-2 h-4 w-4" /> {isEnglish ? "Clear Pages" : "Kosongkan Halaman"}
                  </Button>
                  <Button variant="ghost" size="sm" className={`mb-1 w-full justify-start ${
                      editorTheme === "dark"
                        ? "text-white hover:bg-[#263b60] hover:text-white"
                        : "text-slate-800 hover:bg-blue-100 hover:text-slate-950"
                    }`} data-testid="editor-delete-pages-button" onClick={() => { setMoreMenuOpen(false); openDeletePagesDialog(); }}>
                    <Trash2 className="mr-2 h-4 w-4" /> {isEnglish ? "Delete Pages" : "Hapus Halaman"}
                  </Button>
                  <Button variant="ghost" size="sm" className={`w-full justify-start ${
                      editorTheme === "dark"
                        ? "text-white hover:bg-[#263b60] hover:text-white"
                        : "text-slate-800 hover:bg-blue-100 hover:text-slate-950"
                    }`} data-testid="editor-save-button" disabled={saveStatus === "saving"} onClick={async () => { setMoreMenuOpen(false); const ok = await save(); if (ok) toast.success(isEnglish ? "Saved" : "Tersimpan"); }}>
                    <Save className="mr-2 h-4 w-4" /> {isEnglish ? "Save" : "Simpan"}
                  </Button>
                </div>
              )}
            </div>
            <div className="relative">
              <Button
                variant="outline"
                size="icon"
                className={`relative h-9 w-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white`}
                data-testid="editor-notifications-button"
                aria-label={isEnglish ? "Notifications" : "Notifikasi"}
                title={isEnglish ? "Notifications" : "Notifikasi"}
                onClick={() => {
                  setNotificationPanelOpen((v) => !v);
                  setSettingsMenuOpen(false);
                }}
              >
                <Bell className="h-4 w-4" />
                {unreadNotificationCount > 0 && (
                  <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-background bg-red-500"
                    aria-label={`${unreadNotificationCount} unread notifications`} />
                )}
              </Button>

              {notificationPanelOpen && (
                <div className="absolute right-0 top-full z-50 mt-4 w-80 max-w-[90vw] rounded-xl border border-blue-200 bg-[#f0f6ff] p-3 text-slate-900 shadow-xl shadow-blue-950/20 dark:border-blue-800 dark:bg-[#101d36] dark:text-white">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">
                      {isEnglish ? "Notifications" : "Notifikasi"}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {unreadNotificationCount} {isEnglish ? "unread" : "belum dibaca"}
                    </span>
                  </div>

                  {notifications.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      {isEnglish ? "No notifications yet" : "Belum ada notifikasi"}
                    </p>
                  ) : (
                    <div className="max-h-80 space-y-2 overflow-y-auto">
                      <div className="flex justify-end">
                {notifications.length > 0 && (
                  <button type="button" onClick={clearAllNotifications} className="text-xs font-semibold text-rose-600 underline hover:text-rose-800 dark:text-rose-300 dark:hover:text-rose-200">
                    {isEnglish === "en" ? "Clear all" : "Bersihkan Semua"}
                  </button>
                )}
              </div>
              {notifications.map((item) => {
                                                const statusText = `${item.status || ""} ${item.type || ""} ${item.action || ""}`.toLowerCase();
                                                const isRed = /expired|cancelled|canceled|kedaluwarsa|failed|gagal/.test(statusText);
                                                const isGreen = !isRed && /paid|success|berhasil/.test(statusText);
                                                const cardTone = isRed
                                                  ? "border-rose-200 bg-rose-50/80 dark:border-rose-700 dark:bg-rose-950/30"
                                                  : isGreen
                                                    ? "border-emerald-200 bg-emerald-50/80 dark:border-emerald-700 dark:bg-emerald-950/25"
                                                    : "border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-[#1b2b4b]";
                                                const titleTone = isRed ? "text-rose-700 dark:text-rose-300" : isGreen ? "text-emerald-700 dark:text-emerald-300" : "";
                                                const messageTone = isRed ? "text-rose-700 dark:text-rose-200" : isGreen ? "text-emerald-800 dark:text-emerald-200" : "";
                                                const dotTone = isRed ? "bg-rose-500" : isGreen ? "bg-emerald-500" : "bg-blue-500";
                                                return (
                                                  <div key={item.id} className={`rounded-lg border p-3 ${cardTone} ${item.read_at ? "opacity-70" : ""}`}>
                                                    <div className="flex items-start justify-between gap-2">
                                                      <p className={`break-words text-xs font-semibold ${titleTone}`}>
                                                        {item.title || item.order_id || (isEnglish ? "Notification" : "Notifikasi")}
                                                      </p>
                                                      {!item.read_at && <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${dotTone}`} />}
                                                    </div>
                                                    <p className={`mt-1 text-sm ${messageTone}`}>
                                                      {item.message || `${item.plan_name || (isEnglish ? "Order" : "Pesanan")}${item.status ? ` · ${item.status}` : ""}`}
                                                    </p>
                                                    <p className="mt-1 text-xs text-slate-500 dark:text-blue-200/60">
                                                      {item.created_at
                                                        ? new Date(item.created_at).toLocaleString(isEnglish ? "en-US" : "id-ID")
                                                        : ""}
                                                    </p>
                                                    <div className="mt-2 flex flex-wrap gap-3">
                                                      {item.action === "renewal" && (
                                                        <button
                                                          type="button"
                                                          className="text-xs font-semibold text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
                                                          onClick={() => {
                                                            markNotificationRead(item.id);
                                                            localStorage.setItem("pms-renewal-open", "1");
                                                            navigate("/");
                                                          }}
                                                        >
                                                          {isEnglish ? "Renew API Key" : "Perpanjang API Key"}
                                                        </button>
                                                      )}
                                                      {item.action === "payment_pending" && (
                                                        <button type="button" className="text-xs font-semibold text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100" onClick={() => resumePendingRenewalPayment(item)}>
                                                          {isEnglish ? "Resume Payment" : "Lanjutkan Pembayaran"}
                                                        </button>
                                                      )}
                                                      {!item.read_at && (
                                                        <button
                                                          type="button"
                                                          className="text-xs font-medium text-blue-700 underline hover:text-blue-900 dark:text-blue-300 dark:hover:text-blue-100"
                                                          onClick={() => markNotificationRead(item.id)}
                                                        >
                                                          {isEnglish ? "Mark as read" : "Tandai sudah dibaca"}
                                                        </button>
                                                      )}
                                                    </div>
                                                  </div>
                                                );
                                              })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <Button variant="outline" size="icon"
                className={`h-9 w-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white`}
                data-testid="editor-settings-button"
                aria-label={isEnglish ? "Settings" : "Pengaturan"}
                title={isEnglish ? "Settings" : "Pengaturan"}
                onClick={() => {
                  setSettingsGearRotation(v => v + (settingsMenuOpen ? -360 : 360));
                  setSettingsMenuOpen(v => !v);
                }}>
                <svg viewBox="0 0 24 24"
                  className="h-[22px] w-[22px] transition-transform duration-1500 ease-in-out"
                  style={{ transform: `rotate(${settingsGearRotation}deg)` }}
                  fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" clipRule="evenodd"
                    d="M12,15.5A3.5,3.5 0 1,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.98C19.47,12.66 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11.02L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.97 19.04,5.05L16.56,6.05C16.04,5.66 15.47,5.34 14.85,5.11L14.48,2.48C14.44,2.24 14.23,2.06 13.98,2.06H10.02C9.77,2.06 9.56,2.24 9.52,2.48L9.15,5.11C8.53,5.34 7.96,5.66 7.44,6.05L4.96,5.05C4.73,4.97 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11.02C4.53,11.34 4.5,11.67 4.57,12.98L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.96,18.95L7.44,17.95C7.96,18.34 8.53,18.66 9.15,18.89L9.52,21.52C9.56,21.76 9.77,21.94 10.02,21.94H13.98C14.23,21.94 14.44,21.76 14.48,21.52L14.85,18.89C15.47,18.66 16.04,18.34 16.56,17.95L19.04,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.98Z" />
                </svg>
              </Button>
              {settingsMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-4 w-72 rounded-2xl border border-blue-200 bg-[#f0f6ff] p-4 text-slate-900 shadow-xl shadow-blue-950/20 animate-in fade-in-0 zoom-in-95 duration-200 dark:border-blue-800 dark:bg-[#101d36] dark:text-white">
                  <p className="mb-3 text-sm font-semibold">{isEnglish ? "Settings" : "Pengaturan"}</p>
                  <div className="mb-2">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-md border border-blue-200 bg-white px-3 py-2 text-left text-slate-700 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900 dark:border-blue-800 dark:bg-[#152541] dark:text-blue-100 dark:hover:border-blue-600 dark:hover:bg-[#1b2b4b] dark:hover:text-white"
                      onClick={() => setOrderPromptSettingsOpen((v) => !v)}
                    >
                      <span className="text-sm font-semibold text-slate-700 dark:text-blue-100">
                        {isEnglish ? "Order Number Input" : "Input Nomor Pesanan"}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-violet-400 transition-transform ${orderPromptSettingsOpen ? "rotate-180" : ""}`} />
                    </button>
                    {orderPromptSettingsOpen && (
                      <div className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-800">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              {isEnglish ? "Show after image upload" : "Tampilkan setelah upload gambar"}
                            </p>
                            <p className="mt-2 text-xs font-medium text-blue-700 dark:text-blue-300">
                              {orderPromptEnabled ? (isEnglish ? "Active" : "Aktif") : (isEnglish ? "Inactive" : "Nonaktif")}
                            </p>
                          </div>
                          <Switch
                            checked={orderPromptEnabled}
                            onCheckedChange={(checked) => {
                              setOrderPromptEnabled(checked);
                              localStorage.setItem("pinmaker_editor_order_prompt", checked ? "on" : "off");
                            }}
                            data-testid="editor-order-prompt-toggle"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <Button variant="outline" size="sm" className="mb-3 w-full justify-start border-blue-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900 dark:border-blue-800 dark:bg-[#152541] dark:text-blue-100 dark:hover:border-blue-600 dark:hover:bg-[#1b2b4b] dark:hover:text-white"
                    onClick={() => { setSettingsMenuOpen(false); setPresetEditorOpen(true); }}
                    data-testid="editor-preset-settings-button">
                    <Ruler className="mr-2 h-4 w-4 text-rose-400" /> Pengaturan Preset Pin
                  </Button>
                  <Button variant="outline" size="sm" className="mb-3 w-full justify-start border-blue-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900 dark:border-blue-800 dark:bg-[#152541] dark:text-blue-100 dark:hover:border-blue-600 dark:hover:bg-[#1b2b4b] dark:hover:text-white"
                    onClick={() => setAutoSaveSettingsOpen((v) => !v)}
                    data-testid="editor-autosave-settings-button">
                    <Save className="mr-2 h-4 w-4 text-cyan-400" /> Auto Save
                  </Button>
                  {autoSaveSettingsOpen && (
                    <div className="mt-2 mb-3 rounded-lg border border-blue-200 bg-white/80 p-3 dark:border-blue-800 dark:bg-[#152541]">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">Auto Save</p>
                          <p className="text-xs text-muted-foreground">
                            {autoSaveOn ? (isEnglish ? "Auto save is on" : "Penyimpanan otomatis aktif") : (isEnglish ? "Save changes manually" : "Simpan perubahan secara manual")}
                          </p>
                        </div>
                        <Switch checked={autoSaveOn} onCheckedChange={toggleAutoSave}
                          data-testid="editor-autosave-toggle" />
                      </div>
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="mb-3 w-full justify-start border-blue-200 bg-white text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900 dark:border-blue-800 dark:bg-[#152541] dark:text-blue-100 dark:hover:border-blue-600 dark:hover:bg-[#1b2b4b] dark:hover:text-white"
                    onClick={() => setEditorThemeSettingsOpen((v) => !v)}
                    data-testid="editor-theme-settings-button">
                    <Palette className="mr-2 h-4 w-4 text-violet-400" /> Theme Settings
                  </Button>
                  {editorThemeSettingsOpen && (
                    <div className="mt-2 mb-3 rounded-lg border border-blue-200 bg-white/80 p-3 dark:border-blue-800 dark:bg-[#152541]">
                      <p className="mb-2 text-sm font-medium">{isEnglish ? "Choose Theme" : "Pilih Tema"}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button type="button" size="sm"
                          variant={editorTheme === "light" ? "default" : "outline"}
                          className={editorTheme === "light" ? "pms-theme-choice pms-theme-choice-active" : "pms-theme-choice pms-theme-choice-inactive"}
                          onClick={async () => {
                            setEditorTheme("light");
                            localStorage.setItem("pinmaker-login-theme", "light");
                            localStorage.setItem("pms-theme", "light");
                            document.documentElement.classList.remove("dark");
                            window.dispatchEvent(new CustomEvent("pms-theme-change", { detail: { theme: "light" } }));
                            await persistUserSettings({ theme: "light" });
                          }}>
                          Terang
                        </Button>
                        <Button type="button" size="sm"
                          variant={editorTheme === "dark" ? "default" : "outline"}
                          className={editorTheme === "dark" ? "pms-theme-choice pms-theme-choice-active" : "pms-theme-choice pms-theme-choice-inactive"}
                          onClick={async () => {
                            setEditorTheme("dark");
                            localStorage.setItem("pinmaker-login-theme", "dark");
                            localStorage.setItem("pms-theme", "dark");
                            document.documentElement.classList.add("dark");
                            window.dispatchEvent(new CustomEvent("pms-theme-change", { detail: { theme: "dark" } }));
                            await persistUserSettings({ theme: "dark" });
                          }}>
                          Gelap
                        </Button>
                      </div>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" className="pms-settings-close-button mt-5 w-full"
                    onClick={() => setSettingsMenuOpen(false)}>{isEnglish ? "Close" : "Tutup"}</Button>
                </div>
              )}
            </div>
            <Separator orientation="vertical" className="h-6" />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className={`h-9 rounded-xl border border-blue-800 bg-[#1b2b4b] text-blue-100 hover:border-cyan-400 hover:bg-[#263b60] hover:text-white`} data-testid="editor-print-button"
                  disabled={busy} onClick={doPrint}>
                  <Printer className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isEnglish ? "Print (actual A4 size)" : "Cetak (A4 ukuran asli)"}</TooltipContent>
            </Tooltip>
            <Button className="h-9 rounded-xl border border-blue-500 bg-blue-600 font-semibold text-white hover:bg-blue-700" data-testid="editor-export-pdf-button"
              disabled={busy} onClick={doExport}>
              <Download className="mr-2 h-4 w-4" /> {isEnglish ? "Export PDF" : "Ekspor PDF"}
            </Button>
          </div>
        </header>

        {/* Unsaved-changes confirmation when leaving the editor */}
        <AlertDialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
          <AlertDialogContent
            data-testid="unsaved-leave-dialog"
            className={editorTheme === "dark"
              ? "!border-[#29466f] !bg-[#07132e] !text-slate-100"
              : "!border-[#c7dcf8] !bg-[#f0f6ff] !text-slate-900"}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{isEnglish ? "Unsaved changes" : "File belum disimpan"}</AlertDialogTitle>
              <AlertDialogDescription
                className={editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}
              >
                {isEnglish ? "You have unsaved changes. Save before leaving?" : "Ada perubahan yang belum disimpan. Simpan dulu sebelum keluar?"}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="leave-cancel-btn"
                className={editorTheme === "dark"
                  ? "!border-[#29466f] !bg-[#0b1733] !text-slate-200 hover:!bg-[#152541] hover:!text-white"
                  : "!border-slate-300 !bg-white !text-slate-700 hover:!bg-[#dbeafe] hover:!text-[#17345d]"}
              >{isEnglish ? "Cancel" : "Batal"}</AlertDialogCancel>
              <Button
                variant="outline"
                data-testid="leave-without-save-btn"
                className={editorTheme === "dark"
                  ? "!border-red-400/70 !bg-red-500/10 !text-red-300 hover:!bg-red-500/20 hover:!text-red-200"
                  : "!border-red-300 !bg-red-50 !text-red-700 hover:!bg-red-100"}
                onClick={() => { setLeaveDialogOpen(false); navigate("/"); }}>
                {isEnglish ? "Leave Without Saving" : "Keluar Tanpa Simpan"}
              </Button>
              <AlertDialogAction
                data-testid="leave-save-btn"
                className="!rounded-xl !border !border-blue-400 !bg-blue-600 !font-semibold !text-white hover:!bg-blue-500"
                onClick={async () => {
                  const ok = await save();
                  setLeaveDialogOpen(false);
                  if (ok) navigate("/");
                }}>
                {isEnglish ? "Save & Exit" : "Simpan & Keluar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="relative flex min-h-0 flex-1">
          {/* Left rail */}
          {showLeftRail && (
          <aside style={{ backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff", borderColor: editorTheme === "dark" ? "#29466f" : "#c7dcf8" }} className="hidden w-64 shrink-0 flex-col border-r lg:flex">
            <ScrollArea className="flex-1">
              <div className="space-y-5 p-4">
                <div
                  ref={dropRef}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  data-testid="editor-upload-dropzone"
                  style={{
                    backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff",
                    borderColor: editorTheme === "dark" ? "#29466f" : "#b9d2f5",
                    color: editorTheme === "dark" ? "#e2e8f0" : "#17345d"
                  }}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed p-6 text-center transition-colors ${
                    dragOver ? "border-accent bg-accent/5" : "bg-card/60 hover:bg-card"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <p className="mt-2 text-sm font-medium">{isEnglish ? "Drag & drop images" : "Tarik & lepas gambar"}</p>
                </div>

                <div
                  className="rounded-xl p-2"
                  style={{
                    backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff"
                  }}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-wide text-muted-foreground">{isEnglish ? "PAGES" : "HALAMAN"}</span>
                    <span className="font-mono-num text-xs text-muted-foreground">
                      {stats.filled}/{stats.capacity}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {pages.map((pg, pi) => {
                      const filled = pg.cells.filter((c) => c.type !== "empty").length;
                      return (
                        <div
                          key={pi}
                          data-testid={`editor-page-thumb-${pi}`}
                          onClick={() => setCurrentPage(pi)}
                          className={`group/page relative cursor-pointer overflow-hidden rounded-lg border bg-white px-1 py-2 text-left text-gray-900 transition-colors ${
                            currentPage === pi ? "border-primary ring-1 ring-primary" : "border-gray-300"
                          }`}
                        >
                          {pages.length > 1 && (
                            <button
                              type="button"
                              data-testid={`editor-delete-page-${pi}`}
                              onClick={(e) => { e.stopPropagation(); setDeletePageIdx(pi); }}
                              className="absolute right-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-md bg-card/90 text-destructive opacity-0 shadow-sm transition-opacity hover:bg-destructive hover:text-destructive-foreground group-hover/page:opacity-100"
                              title={isEnglish ? "Delete page" : "Hapus halaman"}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <div className="mx-auto" style={{ width: "100%", maxWidth: 88 }}>
                            <MiniPage projectId={id} page={pg} layout={layout} width={88} showCenter={showCenter} centerDotSize={centerDotSize} centerDotColor={centerDotColor} />
                          </div>
                          <div className="mt-1.5 flex items-center gap-1 text-[10px] text-gray-700">
                            <Layers className="h-3 w-3" /> {isEnglish ? "Page" : "Hal"} {pi + 1} · {filled}/15
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Button variant="ghost" size="sm" className="mt-3 w-full rounded-xl"
                    onClick={addPage} data-testid="editor-add-page-rail-button">
                    <FilePlus2 className="mr-2 h-4 w-4" /> {isEnglish ? "Add page" : "Tambah halaman"}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </aside>
          )}

          {/* Arrow toggle on the rail boundary (red-mark position) */}
          <button
            type="button"
            data-testid="editor-toggle-left-rail-button"
            onClick={toggleLeftRail}
            title={isEnglish ? (showLeftRail ? "Hide page list" : "Show page list") : (showLeftRail ? "Sembunyikan daftar halaman" : "Tampilkan daftar halaman")}
            className="absolute top-1/2 z-30 hidden h-14 w-6 -translate-y-1/2 items-center justify-center rounded-r-xl border border-l-0 shadow-md transition-colors lg:flex"
            style={{ left: showLeftRail ? 256 : 0, backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff", borderColor: editorTheme === "dark" ? "#29466f" : "#a9c9f0", color: editorTheme === "dark" ? "#ffffff" : "#17345d" }}
          >
            {showLeftRail ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>

          {/* Canvas */}
          <main
            ref={canvasScrollRef}
            onWheel={onCanvasWheel}
            className="relative min-w-0 flex-1 overflow-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            style={{ backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff" }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => setSelected(null)}
          >
            {/* zoom controls */}
            <div className="sticky top-0 z-20 flex items-center justify-between gap-2 bg-transparent px-4 py-3">
              <span data-testid="editor-page-indicator"
                className="rounded-full px-3 py-1 text-xs font-medium shadow-sm"
                style={{
                  backgroundColor: editorTheme === "dark" ? "#152541" : "#ffffff",
                  color: editorTheme === "dark" ? "#dbeafe" : "#263b60",
                  border: editorTheme === "dark" ? "1px solid #29466f" : "1px solid #dbeafe"
                }}>
                {isEnglish ? "Page" : "Halaman"} {currentPage + 1} / {pages.length}
              </span>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center justify-center gap-1 rounded-full px-1 py-1 shadow-sm"
                  style={{
                    width: 120,
                    height: 36,
                    backgroundColor: editorTheme === "dark" ? "#152541" : "#ffffff",
                    color: editorTheme === "dark" ? "#dbeafe" : "#263b60",
                    border: editorTheme === "dark" ? "1px solid #29466f" : "1px solid #dbeafe"
                  }}>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                    data-testid="editor-view-zoom-out" onClick={(e) => { e.stopPropagation(); setViewScale((v) => Math.max(0.4, +(v - 0.1).toFixed(2))); }}>
                    <ZoomOut className="h-3.5 w-3.5" />
                  </Button>
                  <span className="w-12 text-center font-mono-num text-xs">{Math.round(viewScale * 100)}%</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                    data-testid="editor-view-zoom-in" onClick={(e) => { e.stopPropagation(); setViewScale((v) => Math.min(1.8, +(v + 0.1).toFixed(2))); }}>
                    <ZoomIn className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <div data-testid="editor-page-navigation"
                  className="flex items-center justify-between rounded-full px-1 shadow-sm"
                  style={{
                    width: 120,
                    height: 36,
                    backgroundColor: editorTheme === "dark" ? "#152541" : "#ffffff",
                    color: editorTheme === "dark" ? "#dbeafe" : "#263b60",
                    border: editorTheme === "dark" ? "1px solid #29466f" : "1px solid #dbeafe"
                  }}>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                    disabled={currentPage <= 0}
                    aria-label="Halaman sebelumnya"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage((c) => Math.max(c - 1, 0)); }}>
                    <span className="text-xl leading-none">‹</span>
                  </Button>

                  <input
                    key={currentPage}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    defaultValue={currentPage + 1}
                    aria-label="Masukkan nomor halaman"
                    title={`Masukkan nomor halaman (1-${pages.length}) lalu tekan Enter`}
                    className="h-6 w-8 rounded-md border bg-transparent p-0 text-center font-mono-num text-xs font-semibold outline-none focus:ring-0"
                    style={{
                      color: editorTheme === "dark" ? "#dbeafe" : "#263b60",
                      backgroundColor: "transparent",
                      borderColor: editorTheme === "dark" ? "#29466f" : "#dbeafe",
                      appearance: "textfield"
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        const page = Number(e.currentTarget.value);
                        if (Number.isInteger(page) && page >= 1 && page <= pages.length) {
                          setCurrentPage(page - 1);
                        } else {
                          e.currentTarget.value = String(currentPage + 1);
                        }
                        e.currentTarget.blur();
                      }
                    }}
                    onBlur={(e) => {
                      const page = Number(e.currentTarget.value);
                      if (Number.isInteger(page) && page >= 1 && page <= pages.length) {
                        setCurrentPage(page - 1);
                      } else {
                        e.currentTarget.value = String(currentPage + 1);
                      }
                    }}
                  />

                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full"
                    disabled={currentPage >= pages.length - 1}
                    aria-label="Halaman berikutnya"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage((c) => Math.min(c + 1, pages.length - 1)); }}>
                    <span className="text-xl leading-none">›</span>
                  </Button>
                </div>
              </div>
            </div>

            <div
              className="flex justify-center px-6 pb-16 pt-2"
              style={{
                transform: showLeftRail ? "translateX(0)" : "translateX(128px)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {pages[currentPage] && (
                <PinCanvas
                  projectId={id}
                  page={pages[currentPage]}
                  pageIdx={currentPage}
                  layout={layout}
                  ppmm={ppmm}
                  selected={selected}
                  nextEmpty={nextEmpty}
                  showCenter={showCenter}
                  centerDotSize={centerDotSize}
                  centerDotColor={centerDotColor}
                  onSelectCell={(pi, ci) => setSelected({ pageIdx: pi, cellIdx: ci })}
                  onChangeTransform={onChangeTransform}
                />
              )}
            </div>
          </main>

          {/* Inspector */}
          <aside style={{ backgroundColor: editorTheme === "dark" ? "#07132e" : "#f0f6ff", borderColor: editorTheme === "dark" ? "#29466f" : "#c7dcf8" }} className="hidden w-80 shrink-0 border-l p-4 md:block">
            <Inspector
              theme={editorTheme}
              language={language}
              selected={selected}
              cell={selectedCell}
              defaultFont={defaultFont}
              onUploadImage={handleUploadToCell}
              onSetText={handleSetText}
              onSetColor={handleSetColor}
              onDelete={handleDelete}
              onChangeTransform={(patch) => onChangeTransform(selected.pageIdx, selected.cellIdx, patch)}
              onResetTransform={handleReset}
              onCrop={() => setCropOpen(true)}
              onDuplicate={() => { setDupCount("5"); setDupOpen(true); }}
            />
          </aside>
        </div>

        {/* Image crop dialog */}
        <CropDialog
          open={cropOpen}
          src={cropSrc}
          theme={editorTheme}
          onClose={() => setCropOpen(false)}
          onApply={handleApplyCrop}
        />

        {/* Duplicate selected image dialog */}
        <Dialog open={dupOpen} onOpenChange={setDupOpen}>
          <DialogContent className={`max-w-lg ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100 [&>button]:text-slate-300 [&>button:hover]:bg-[#152541]" : "border-[#bfdbfe] bg-[#eff6ff] text-[#17345d] [&>button]:text-[#17345d] [&>button:hover]:bg-[#dbeafe]"}`} data-testid="duplicate-image-dialog">
            <DialogHeader>
              <DialogTitle>Duplikat gambar</DialogTitle>
              <DialogDescription className={editorTheme === "dark" ? "text-slate-400" : "text-[#64748b]"}>
                Salinan disisipkan tepat setelah gambar ini. Isi setelahnya (mis. teks
                nomor pesanan) otomatis bergeser mundur. Halaman baru ditambahkan bila penuh.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="dup-count" className={editorTheme === "dark" ? "text-slate-200" : "text-[#17345d]"}>Jumlah salinan</Label>
              <Input
                id="dup-count"
                type="number"
                min={1}
                data-testid="duplicate-count-input"
                className={editorTheme === "dark" ? "border-[#29466f] bg-[#0b1933] text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500" : "border-[#bfdbfe] bg-white text-[#17345d] placeholder:text-[#94a3b8] focus-visible:ring-[#93c5fd]"}
                value={dupCount}
                onChange={(e) => setDupCount(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleDuplicateImage(dupCount); }}
                autoFocus
              />
              <p className={editorTheme === "dark" ? "text-xs text-slate-400" : "text-xs text-[#64748b]"}>
                Setiap salinan membawa pengaturan zoom, geser & crop yang sama. Tanpa batas jumlah.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" className={editorTheme === "dark" ? "text-slate-200 hover:bg-[#152541]" : "text-[#17345d] hover:bg-[#dbeafe]"} onClick={() => setDupOpen(false)} data-testid="duplicate-cancel-button">
                Batal
              </Button>
              <Button className="bg-[#3475e8] text-white hover:bg-[#2563eb]" onClick={() => handleDuplicateImage(dupCount)} data-testid="duplicate-confirm-button">
                <CopyPlus className="mr-2 h-4 w-4" /> Duplikat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auto order-number prompt after image batch */}
        <Dialog open={orderPromptOpen} onOpenChange={(o) => { if (!o) skipOrderNumber(); }}>
          <DialogContent data-testid="order-number-dialog"
            className={`w-[calc(100vw-2rem)] max-w-md rounded-2xl border shadow-[0_24px_80px_rgba(0,0,0,0.55)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 duration-200 ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-slate-200 bg-white text-slate-900"}`}>
            <DialogHeader>
              <DialogTitle className={editorTheme === "dark" ? "text-xl font-extrabold tracking-tight text-slate-100" : "text-xl font-extrabold tracking-tight text-slate-900"}>Masukkan nomor pesanan</DialogTitle>
              <DialogDescription className={editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}>
                {lastBatchCount} gambar telah ditempatkan. Tambahkan nomor pesanan ke posisi kosong
                berikutnya, atau lewati.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="order-number" className={editorTheme === "dark" ? "text-sm font-medium text-slate-200" : "text-sm font-medium text-slate-700"}>Nomor pesanan</Label>
              <Input
                className={editorTheme === "dark" ? "border-[#29466f] bg-[#0b1733] text-slate-100 placeholder:text-slate-500 focus-visible:ring-blue-500" : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-blue-500"}
                id="order-number"
                data-testid="order-number-input"
                placeholder="mis. ORD-1023"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmOrderNumber(); }}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" className={editorTheme === "dark" ? "text-slate-300 hover:bg-[#152541] hover:text-white" : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"} onClick={skipOrderNumber} data-testid="order-number-skip-button">
                Lewati
              </Button>
              <Button className="rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-950/30 hover:bg-blue-500" onClick={confirmOrderNumber} data-testid="order-number-confirm-button"
                disabled={!orderNumber.trim()}>
                Tambahkan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview upload banyak folder */}
        <Dialog
          open={folderUploadDialogOpen}
          onOpenChange={(open) => {
            setFolderUploadDialogOpen(open);
            if (!open && !busy) setFolderUploadGroups([]);
          }}
        >
          <DialogContent
            data-testid="folder-upload-dialog"
            className={`w-[calc(100vw-2rem)] max-h-[85vh] max-w-2xl overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden rounded-2xl border shadow-[0_24px_80px_rgba(0,0,0,0.35)] ${editorTheme === "dark" ? "border-blue-400/20 bg-[#07132e] text-slate-100" : "border-blue-200 bg-[#f0f6ff] text-slate-900"}`}
          >
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold">
                {isEnglish ? "Preview Multiple Folder Upload" : "Preview Upload Banyak Folder"}
              </DialogTitle>
              <DialogDescription className={editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}>
                {isEnglish
                  ? "Each folder becomes one consecutive image group. Choose what information to add after each group."
                  : "Setiap folder menjadi satu kelompok foto yang berurutan. Pilih informasi yang akan ditambahkan setelah setiap kelompok."}
              </DialogDescription>
            </DialogHeader>

            <div className={`space-y-2 rounded-xl border p-3 ${editorTheme === "dark" ? "border-blue-400/20 bg-[#0b1733]" : "border-blue-200 bg-white"}`}>
              <label htmlFor="folder-upload-info-mode" className="block text-sm font-semibold">
                {isEnglish ? "Information after each folder" : "Informasi setelah setiap folder"}
              </label>
              <Select
                value={folderUploadInfoMode}
                disabled={busy}
                onValueChange={setFolderUploadInfoMode}
              >
                <SelectTrigger
                  id="folder-upload-info-mode"
                  className={`h-10 w-full rounded-xl border px-3 shadow-none focus:ring-2 focus:ring-blue-500/30 ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-blue-200 bg-white text-slate-900"}`}
                >
                  <SelectValue placeholder={isEnglish ? "Choose information" : "Pilih informasi"} />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  sideOffset={6}
                  className={`z-[100] rounded-xl border p-1 shadow-xl ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-blue-200 bg-white text-slate-900"}`}
                >
                  <SelectItem
                    value="order"
                    className={`cursor-pointer rounded-lg border border-transparent px-3 py-2 outline-none transition-colors ${
                      editorTheme === "dark"
                        ? "text-slate-100 data-[highlighted]:bg-white/5 data-[highlighted]:text-white data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-300"
                        : "text-slate-900 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-900 data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-700"
                    }`}
                  >
                    {isEnglish ? "Order Number (last 6 characters)" : "Nomor Pesanan (6 karakter terakhir)"}
                  </SelectItem>
                  <SelectItem
                    value="count"
                    className={`cursor-pointer rounded-lg border border-transparent px-3 py-2 outline-none transition-colors ${
                      editorTheme === "dark"
                        ? "text-slate-100 data-[highlighted]:bg-white/5 data-[highlighted]:text-white data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-300"
                        : "text-slate-900 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-900 data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-700"
                    }`}
                  >
                    {isEnglish ? "Photo Count (e.g. 5 PCS)" : "Jumlah Foto (misalnya 5 PCS)"}
                  </SelectItem>
                  <SelectItem
                    value="sequence"
                    className={`cursor-pointer rounded-lg border border-transparent px-3 py-2 outline-none transition-colors ${
                      editorTheme === "dark"
                        ? "text-slate-100 data-[highlighted]:bg-white/5 data-[highlighted]:text-white data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-300"
                        : "text-slate-900 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-900 data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-700"
                    }`}
                  >
                    {isEnglish ? "Sequence Number (00001)" : "Nomor Urut (00001)"}
                  </SelectItem>
                  <SelectItem
                    value="none"
                    className={`cursor-pointer rounded-lg border border-transparent px-3 py-2 outline-none transition-colors ${
                      editorTheme === "dark"
                        ? "text-slate-100 data-[highlighted]:bg-white/5 data-[highlighted]:text-white data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-300"
                        : "text-slate-900 data-[highlighted]:bg-blue-50 data-[highlighted]:text-blue-900 data-[state=checked]:!border-blue-500 data-[state=checked]:!bg-transparent data-[state=checked]:!text-blue-700"
                    }`}
                  >
                    {isEnglish ? "No Information" : "Tanpa Informasi"}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className={`text-xs ${editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                {folderUploadInfoMode === "order"
                  ? (isEnglish ? "Uses the last six letters or numbers of each folder name." : "Menggunakan enam karakter terakhir berupa huruf atau angka dari nama setiap folder.")
                  : folderUploadInfoMode === "count"
                    ? (isEnglish ? "Adds the number of photos in each folder, followed by PCS." : "Menambahkan jumlah foto di setiap folder dengan akhiran PCS.")
                    : folderUploadInfoMode === "sequence"
                      ? (isEnglish ? "Adds a five-digit sequence starting from 00001." : "Menambahkan nomor urut lima digit mulai dari 00001.")
                      : (isEnglish ? "Only images will be inserted. No text cells will be added." : "Hanya foto yang dimasukkan. Tidak ada sel teks tambahan.")}
              </p>
            </div>


            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              <span className={`rounded-lg px-3 py-2 ${editorTheme === "dark" ? "bg-[#152541] text-slate-200" : "border border-blue-200 bg-white text-slate-700"}`}>
                {folderUploadGroups.length} {isEnglish ? "folders" : "folder"}
              </span>
              <span className={`rounded-lg px-3 py-2 ${editorTheme === "dark" ? "bg-[#152541] text-slate-200" : "border border-blue-200 bg-white text-slate-700"}`}>
                {folderUploadGroups.reduce((n, group) => n + group.files.length, 0)} {isEnglish ? "images" : "gambar"}
              </span>
              <span className={`rounded-lg px-3 py-2 ${editorTheme === "dark" ? "bg-[#152541] text-slate-200" : "border border-blue-200 bg-white text-slate-700"}`}>
                {folderUploadInfoMode === "none"
                  ? (isEnglish ? "No information" : "Tanpa informasi")
                  : `${folderUploadGroups.length} ${isEnglish ? "information labels" : "label informasi"}`}
              </span>
            </div>

            <div className="max-h-[45vh] space-y-2 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden pr-1 pms-folder-preview-list">
              {folderUploadGroups.map((group, index) => (
                <div
                  key={`${group.folderName}-${index}`}
                  className={`rounded-xl border p-3 ${editorTheme === "dark" ? "border-blue-400/20 bg-[#0b1733]" : "border-blue-200 bg-white"}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className={`break-all text-sm font-semibold ${editorTheme === "dark" ? "text-white" : "text-slate-900"}`}>
                        {group.folderName}
                      </p>
                      <p className={`mt-1 text-xs ${editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                        {group.files.length} {isEnglish ? "images" : "foto"}
                      </p>
                    </div>
                    <div className="text-right">
                      {folderUploadInfoMode === "none" ? null : folderUploadInfoMode === "order" && !group.orderNumber ? (
                        <span className="inline-flex rounded-lg bg-red-500/15 px-3 py-1.5 text-xs font-semibold text-red-300">
                          {isEnglish ? "Invalid folder number" : "Nomor tidak ditemukan"}
                        </span>
                      ) : (
                        <span className={`inline-flex rounded-lg px-3 py-1.5 font-mono text-sm font-bold tracking-wider ${editorTheme === "dark" ? "bg-blue-500/15 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                          {folderUploadInfoMode === "order"
                            ? group.orderNumber
                            : folderUploadInfoMode === "count"
                              ? `${group.files.length} PCS`
                              : String(index + 1).padStart(5, "0")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {folderUploadInfoMode === "order" && folderUploadGroups.some((group) => !group.orderNumber) && (
              <p className="text-xs text-red-300">
                {isEnglish
                  ? "Ensure the last six characters of each folder name are letters or numbers."
                  : "Pastikan enam karakter terakhir setiap nama folder berupa huruf atau angka."}
              </p>
            )}

            <DialogFooter>
              <Button
                variant="ghost"
                className={editorTheme === "dark" ? "text-slate-300 hover:bg-[#152541] hover:text-white" : "text-[#17345d] hover:bg-[#dbeafe]"}
                disabled={busy}
                onClick={() => {
                  setFolderUploadDialogOpen(false);
                  setFolderUploadGroups([]);
                }}
              >
                {isEnglish ? "Cancel" : "Batal"}
              </Button>
              <Button
                className="rounded-xl bg-blue-600 text-white hover:bg-blue-500"
                data-testid="folder-upload-confirm-button"
                disabled={
                  busy ||
                  !folderUploadGroups.length ||
                  (folderUploadInfoMode === "order" && folderUploadGroups.some((group) => !group.orderNumber))
                }
                onClick={confirmFolderUpload}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isEnglish ? "Upload All" : "Upload Semua"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Peringatan setelah Upload Banyak Folder berhasil */}
        <Dialog
          open={folderUploadNoticeOpen}
          onOpenChange={setFolderUploadNoticeOpen}
        >
          <DialogContent
            data-testid="folder-upload-notice-dialog"
            className={`w-[calc(100vw-2rem)] max-w-[512px] min-h-[326px] flex flex-col justify-between rounded-2xl border p-6 shadow-[0_24px_80px_rgba(0,0,0,0.35)] ${editorTheme === "dark" ? "border-blue-400/30 bg-[#07132e] text-slate-100" : "border-blue-200 bg-[#f0f6ff] text-slate-900"}`}
          >
            <DialogHeader className="flex flex-1 flex-col items-center justify-center space-y-4 text-center sm:text-center">
              <DialogTitle className="text-center text-3xl sm:text-4xl font-extrabold text-red-500">
                {isEnglish ? "Warning!" : "Peringatan!"}
              </DialogTitle>
              <DialogDescription className={`mx-auto max-w-[430px] text-center text-base font-normal leading-relaxed ${editorTheme === "dark" ? "text-slate-200" : "text-slate-700"}`}>
                {isEnglish
                  ? "Please review the uploaded images. Make sure each image is correctly positioned and no part is cropped. Thank you."
                  : "Silakan periksa kembali gambar yang sudah diunggah. Pastikan setiap gambar berada pada posisi yang sesuai dan tidak ada bagian yang terpotong. Terima kasih."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-auto">
              <Button
                type="button"
                className="rounded-xl bg-blue-600 px-6 font-semibold text-white hover:bg-blue-500"
                onClick={() => setFolderUploadNoticeOpen(false)}
              >
                {isEnglish ? "OK" : "Oke"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Account-level pin preset settings */}
        <Dialog open={presetEditorOpen} onOpenChange={setPresetEditorOpen}>
          <DialogContent data-testid="pin-preset-dialog" className={`w-[calc(100vw-2rem)] max-h-[90vh] max-w-4xl overflow-y-auto rounded-2xl border shadow-[0_24px_80px_rgba(0,0,0,0.55)] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 duration-200 ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-slate-200 bg-[#f0f6ff] text-slate-900"}`}>
            <DialogHeader>
              <DialogTitle className="text-xl font-extrabold tracking-tight">{isEnglish ? "Pin Preset Settings" : "Pengaturan Preset Pin"}</DialogTitle>
              <DialogDescription className={editorTheme === "dark" ? "text-slate-400" : "text-slate-600"}>
                {isEnglish
                  ? "Customize pin sizes and spacing saved to this account."
                  : "Atur ukuran dan jarak pin yang disimpan khusus untuk akun ini."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Ukuran preset</label>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {["25", "32", "44", "58", "75"].map((size) => (
                    <Button key={size} type="button"
                      variant={gridPreset === size ? "default" : "outline"}
                      onClick={() => setGridPreset(size)}
                      className={gridPreset === size
                        ? "h-11 rounded-xl border border-blue-400 bg-blue-600 font-bold text-white shadow-lg shadow-blue-950/30 transition-colors duration-200 hover:bg-blue-500"
                        : editorTheme === "dark"
                          ? "h-11 rounded-xl border border-[#29466f] bg-[#07132e] font-semibold text-slate-300 transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:bg-[#0b1733] hover:text-white"
                          : "h-11 rounded-xl border border-blue-200 bg-[#f0f6ff] font-semibold text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-900"}>
                      {size} MM
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="editor-preset-inner" className="mb-1 block text-sm">Diameter dalam (mm)</label>
                  <NumberStepper
                    id="editor-preset-inner" min={4} step={0.5}
                    value={innerD} onChange={setInnerD}
                    theme={editorTheme}
                  />
                </div>
                <div>
                  <label htmlFor="editor-preset-outer" className="mb-1 block text-sm">Diameter luar (mm)</label>
                  <NumberStepper
                    id="editor-preset-outer" min={4} step={0.5}
                    value={outerD} onChange={setOuterD}
                    theme={editorTheme}
                  />
                </div>
                <div>
                  <label htmlFor="editor-preset-gap-h" className="mb-1 block text-sm">Jarak X / horizontal (mm)</label>
                  <NumberStepper
                    id="editor-preset-gap-h" min={0} step={0.5}
                    value={gapH}
                    onChange={(value) => setGapH(Math.max(0, Number(value)))}
                    theme={editorTheme}
                  />
                </div>
                <div>
                  <label htmlFor="editor-preset-gap-v" className="mb-1 block text-sm">{isEnglish ? "Y / vertical spacing (mm)" : "Jarak Y / vertikal (mm)"}</label>
                  <NumberStepper
                    id="editor-preset-gap-v" min={0} step={0.5}
                    value={gapV}
                    onChange={(value) => setGapV(Math.max(0, Number(value)))}
                    theme={editorTheme}
                  />
                </div>
              </div>
              <div className={editorTheme === "dark" ? "space-y-3 rounded-xl border border-[#29466f] bg-[#0b1733]/80 p-4 shadow-inner shadow-blue-950/20" : "space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4"}>
                <label className="flex items-center gap-3">
                  <Checkbox checked={showCenter} onCheckedChange={(v) => setShowCenter(!!v)} />
                  <span className="text-sm font-medium">{isEnglish ? "Enable red dots above the circles" : "Aktifkan dot merah di atas lingkaran"}</span>
                </label>
                {showCenter && (
                  <div>
                    <label htmlFor="editor-preset-dot-size" className="mb-1 block text-sm">{isEnglish ? "Red dot size (mm)" : "Ukuran dot merah (mm)"}</label>
                    <NumberStepper
                      id="editor-preset-dot-size" min={0.5} max={8} step={0.1}
                      value={centerDotSize} onChange={setCenterDotSize}
                      theme={editorTheme}
                    />
                  </div>
                )}
              </div>
              <p className={editorTheme === "dark" ? "rounded-xl border border-blue-400/10 bg-blue-500/5 p-3 text-xs leading-relaxed text-slate-400" : "rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs leading-relaxed text-slate-600"}>
                {isEnglish
                  ? "Preset settings are saved to this account and kept separate from other accounts."
                  : "Pengaturan preset disimpan khusus untuk akun ini dan terpisah dari akun lain."}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className={editorTheme === "dark" ? "rounded-xl border-slate-600 text-slate-200 transition-colors hover:border-blue-400 hover:bg-slate-800" : "rounded-xl border-slate-300 text-slate-700 transition-colors hover:border-blue-400 hover:bg-blue-50"} onClick={() => setPresetEditorOpen(false)}>{isEnglish ? "Cancel" : "Batal"}</Button>
              <Button type="button"
                className="pms-editor-preset-save-button"
                disabled={!Number.isFinite(innerD) || !Number.isFinite(outerD) || innerD <= 0 || outerD <= innerD}
                onClick={async () => {
                  try {
                    const response = await api.get("/user/settings");
                    const savedSettings = response.data || {};
                    const savedPresets = savedSettings.pinPresets || {};
                    const size = String(gridPreset);

                    const updatedPreset = {
                      innerD,
                      outerD,
                      gapH,
                      gapV,
                      gridPreset: size,
                      showCenter,
                      centerDotSize,
                      centerDotColor,
                    };

                    const ok = await persistUserSettings({
                      pinPresets: {
                        ...savedPresets,
                        [size]: updatedPreset,
                      },
                      pinPreset: updatedPreset,
                    });

                    if (ok) {
                      setPresetEditorOpen(false);
                      toast.success(isEnglish ? "Account pin presets saved" : "Preset Pin akun berhasil disimpan");
                    }
                  } catch (e) {
                    toast.error(isEnglish ? "Failed to load or save account pin presets" : "Preset Pin akun gagal dimuat atau disimpan");
                  }
                }}>
                {isEnglish ? "Done" : "Selesai"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      <Dialog open={sizeDialogOpen} onOpenChange={setSizeDialogOpen}>
          <DialogContent data-testid="size-dialog" className={`max-h-[90vh] overflow-y-auto rounded-2xl border shadow-2xl ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100 [&_input:not([type=color])]:border-blue-400/20 [&_input:not([type=color])]:bg-[#0b1733] [&_input:not([type=color])]:text-slate-100" : "border-slate-200 bg-[#f0f6ff] text-slate-900 [&_input:not([type=color])]:border-slate-300 [&_input:not([type=color])]:bg-white [&_input:not([type=color])]:text-slate-900"}`}>
            <DialogHeader>
              <DialogTitle>{isEnglish ? "Circle Size" : "Ukuran lingkaran"}</DialogTitle>
              <DialogDescription>
                {isEnglish
                  ? "Set the inner circle diameter (image area) and outer circle diameter (cut line). The number of circles per page adjusts to fit the A4 page."
                  : "Atur diameter lingkaran dalam (area gambar) dan lingkaran luar (garis potong). Jumlah lingkaran per halaman menyesuaikan diameter agar sesuai dengan kertas A4."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="inner-d">{isEnglish ? "Inner circle (mm)" : "Lingkaran dalam (mm)"}</Label>
                <NumberStepper
                  theme={editorTheme}
                  id="inner-d" min={4} max={Math.max(4, outerD - 0.5)} step={0.5}
                  dataTestId="size-inner-input" value={innerD}
                  onChange={setInnerD}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="outer-d">{isEnglish ? "Outer circle (mm)" : "Lingkaran luar (mm)"}</Label>
                <NumberStepper
                  theme={editorTheme}
                  id="outer-d" min={10} max={100} step={0.5}
                  dataTestId="size-outer-input" value={outerD}
                  onChange={setOuterD}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="gap-x">Jarak sumbu X (mm)</Label>
                <NumberStepper
                  theme={editorTheme}
                  id="gap-x" min={0} max={30} step={0.5}
                  dataTestId="gap-x-input" value={gapH}
                  onChange={(value) => setGapH(Math.max(0, Math.min(30, Number(value) || 0)))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="gap-y">Jarak sumbu Y (mm)</Label>
                <NumberStepper
                  theme={editorTheme}
                  id="gap-y" min={0} max={30} step={0.5}
                  dataTestId="gap-y-input" value={gapV}
                  onChange={(value) => setGapV(Math.max(0, Math.min(30, Number(value) || 0)))}
                />
              </div>
            </div>
            <div className="circle-layout-effective rounded-lg border p-3 text-xs">
              Efektif setelah penyesuaian: dalam <span className="font-mono-num font-semibold text-foreground">{layout.INNER_D.toFixed(1)}mm</span> ·
              luar <span className="font-mono-num font-semibold text-foreground">{layout.OUTER_D.toFixed(1)}mm</span> ·
              jarak samping <span className="font-mono-num">{layout.GAP_H.toFixed(1)}mm</span> ·
              jarak bawah <span className="font-mono-num">{layout.GAP_V.toFixed(1)}mm</span>.
              {outerD > layout.OUTER_D + 0.05 && (
                <span className="mt-1 block text-[hsl(var(--warn))]">
                  Ukuran luar efektif {layout.OUTER_D.toFixed(1)}mm agar bulatan tetap muat di A4.
                </span>
              )}
            </div>
            <div className="space-y-3 rounded-lg border p-3">
              <label className="flex items-center gap-3">
                <Checkbox checked={showCenter} onCheckedChange={(v) => setShowCenter(!!v)}
                  data-testid="size-center-toggle" />
                <span className="text-sm">
                  <span className="font-medium">Tampilkan titik penanda (atas)</span>
                  <span className="block text-xs text-muted-foreground">
                    Titik kecil di atas, di antara garis luar &amp; dalam — otomatis di semua lingkaran &amp; halaman.
                  </span>
                </span>
              </label>
              {showCenter && (
                <div className="grid grid-cols-2 gap-4 pl-7">
                  <div className="space-y-1.5">
                    <Label htmlFor="dot-size">Ukuran titik (mm)</Label>
                <NumberStepper
                  theme={editorTheme}
                      id="dot-size" min={0.5} max={8} step={0.1}
                      dataTestId="size-dot-size-input" value={centerDotSize}
                      onChange={setCenterDotSize}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="dot-color">Warna titik</Label>
                    <div className="flex items-center gap-2">
                      <input id="dot-color" type="color"
                        data-testid="size-dot-color-input" value={centerDotColor}
                        onChange={(e) => setCenterDotColor(e.target.value)}
                        className="h-9 w-12 cursor-pointer rounded-md border bg-transparent p-1" />
                      <Input value={centerDotColor}
                        onChange={(e) => setCenterDotColor(e.target.value)}
                        className="h-9 flex-1 font-mono-num" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>

              <Button className="rounded-xl border border-blue-400 bg-blue-600 text-white hover:bg-blue-500" onClick={() => setSizeDialogOpen(false)} data-testid="size-done-button">Selesai</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Default font settings dialog (applies to all text) */}
        <Dialog open={fontDialogOpen} onOpenChange={setFontDialogOpen}>
          <DialogContent data-testid="font-dialog" className={`rounded-2xl border shadow-2xl ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-slate-200 bg-[#f0f6ff] text-slate-900"}`}>
            <DialogHeader>
              <DialogTitle>Font default (semua teks)</DialogTitle>
              <DialogDescription>
                Pengaturan ini berlaku untuk <span className="font-semibold">semua teks</span> —
                yang sudah ada maupun yang akan dibuat.
              </DialogDescription>
            </DialogHeader>
            <TextStyleControls
              idPrefix="default-"
              isDark={editorTheme === "dark"}
              family={defaultFont.fontFamily}
              bold={defaultFont.bold}
              color={defaultFont.color}
              onChange={(patch) => applyDefaultFont(patch)}
            />
            <DialogFooter>
              <Button className="rounded-xl border border-blue-400 bg-blue-600 text-white hover:bg-blue-500" onClick={() => setFontDialogOpen(false)} data-testid="font-done-button">Selesai</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete page confirm */}
        <AlertDialog open={deletePageIdx !== null} onOpenChange={(o) => !o && setDeletePageIdx(null)}>
          <AlertDialogContent data-testid="delete-page-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus halaman {deletePageIdx !== null ? deletePageIdx + 1 : ""}?</AlertDialogTitle>
              <AlertDialogDescription>
                Semua gambar &amp; teks pada halaman ini akan dihapus. Tindakan ini tidak dapat dibatalkan.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-page-cancel">Batal</AlertDialogCancel>
              <AlertDialogAction onClick={() => deletePage(deletePageIdx)} data-testid="delete-page-confirm"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Hapus halaman
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Auto-fill (Penuhi) dialog — per-page selection */}
        <Dialog open={fillDialogOpen} onOpenChange={setFillDialogOpen}>
          <DialogContent data-testid="fill-dialog">
            <DialogHeader>
              <DialogTitle>Penuhi halaman dengan gambar</DialogTitle>
              <DialogDescription>
                Pilih halaman yang ingin dipenuhi. Sel kosong diisi otomatis dengan gambar yang
                sudah ada. Sel berisi teks dibiarkan apa adanya.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              <span className="text-xs font-semibold tracking-wide text-muted-foreground">GAMBAR YANG DIPAKAI</span>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="fill-source-last"
                  onClick={() => setFillSource("last")}
                  className={`rounded-lg border p-2.5 text-left text-sm transition-colors ${
                    fillSource === "last" ? "border-primary bg-primary/5" : "hover:bg-secondary"
                  }`}>
                  <span className="block font-medium">Gambar terakhir</span>
                  <span className="block text-xs text-muted-foreground">1 gambar (nomor terakhir)</span>
                </button>
                <button type="button" data-testid="fill-source-all"
                  onClick={() => setFillSource("all")}
                  className={`rounded-lg border p-2.5 text-left text-sm transition-colors ${
                    fillSource === "all" ? "border-primary bg-primary/5" : "hover:bg-secondary"
                  }`}>
                  <span className="block font-medium">Semua gambar</span>
                  <span className="block text-xs text-muted-foreground">Bergiliran/berurutan</span>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold tracking-wide text-muted-foreground">LINGKARAN YANG DIISI</span>
                <label className="flex items-center gap-2 text-xs font-medium">
                  <Checkbox data-testid="fill-positions-all"
                    checked={fillAllPositions}
                    onCheckedChange={(v) => setFillAllPositions(!!v)} />
                  Semua lingkaran
                </label>
              </div>
              {!fillAllPositions && (
                <div className="grid grid-cols-5 gap-1.5" data-testid="fill-positions-grid">
                  {Array.from({ length: layout.CELLS_PER_PAGE }, (_, i) => (
                    <button key={i} type="button" data-testid={`fill-position-${i}`}
                      onClick={() =>
                        setFillPositionSet((prev) => {
                          const next = new Set(prev);
                          if (next.has(i)) next.delete(i); else next.add(i);
                          return next;
                        })
                      }
                      className={`h-9 rounded-lg border font-mono-num text-sm transition-colors ${
                        fillPositionSet.has(i)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "hover:bg-secondary"
                      }`}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg bg-secondary px-3 py-2">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  data-testid="fill-select-all"
                  checked={pages.length > 0 && fillPageSet.size === pages.length}
                  onCheckedChange={toggleFillAll}
                />
                Pilih semua ({pages.length} halaman)
              </label>
              <span className="font-mono-num text-xs text-muted-foreground">
                {fillPageSet.size} dipilih
              </span>
            </div>

            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
              {pages.map((pg, i) => {
                const filled = pg.cells.filter((c) => c.type !== "empty").length;
                const emptyCount = 15 - filled;
                return (
                  <label
                    key={i}
                    data-testid={`fill-page-option-${i}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      fillPageSet.has(i) ? "border-primary bg-primary/5" : "hover:bg-secondary"
                    }`}
                  >
                    <Checkbox checked={fillPageSet.has(i)} onCheckedChange={() => toggleFillPage(i)} />
                    <div className="shrink-0">
                      <MiniPage projectId={id} page={pg} layout={layout} width={40} showCenter={showCenter}
                        centerDotSize={centerDotSize} centerDotColor={centerDotColor} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Halaman {i + 1}</p>
                      <p className="font-mono-num text-xs text-muted-foreground">
                        {emptyCount > 0 ? `${emptyCount} sel kosong` : "Sudah penuh"}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setFillDialogOpen(false)}
                data-testid="fill-cancel-button">
                Batal
              </Button>
              <Button onClick={runFillImages} disabled={fillPageSet.size === 0}
                data-testid="fill-confirm-button">
                <CopyPlus className="mr-2 h-4 w-4" /> Penuhi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Clear (Kosongkan) dialog — per-page selection */}
        <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
          <DialogContent data-testid="clear-dialog" className={editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-slate-200 bg-white text-slate-900"}>
            <DialogHeader>
              <DialogTitle>Kosongkan halaman</DialogTitle>
              <DialogDescription>
                Semua gambar &amp; teks pada halaman yang dipilih akan dihapus (halaman tetap ada).
              </DialogDescription>
            </DialogHeader>

            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${editorTheme === "dark" ? "border border-[#29466f] bg-[#152541]" : "bg-secondary"}`}>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  data-testid="clear-select-all"
                  checked={pages.length > 0 && clearPageSet.size === pages.length}
                  onCheckedChange={toggleClearAll}
                />
                Pilih semua ({pages.length} halaman)
              </label>
              <span className="font-mono-num text-xs text-muted-foreground">
                {clearPageSet.size} dipilih
              </span>
            </div>

            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1 hide-dialog-scrollbar">
              {pages.map((pg, i) => {
                const filled = pg.cells.filter((c) => c.type !== "empty").length;
                return (
                  <label
                    key={i}
                    data-testid={`clear-page-option-${i}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      clearPageSet.has(i) ? "border-destructive bg-destructive/10" : editorTheme === "dark" ? "border-[#29466f] bg-[#0b1733] hover:bg-[#152541]" : "hover:bg-secondary"
                    }`}
                  >
                    <Checkbox checked={clearPageSet.has(i)} onCheckedChange={() => toggleClearPage(i)} />
                    <div className="shrink-0">
                      <MiniPage projectId={id} page={pg} layout={layout} width={40} showCenter={showCenter}
                        centerDotSize={centerDotSize} centerDotColor={centerDotColor} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Halaman {i + 1}</p>
                      <p className="font-mono-num text-xs text-muted-foreground">{filled}/{layout.CELLS_PER_PAGE} terisi</p>
                    </div>
                  </label>
                );
              })}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setClearDialogOpen(false)}
                data-testid="clear-cancel-button">
                Batal
              </Button>
              <Button onClick={runClearPages} disabled={clearPageSet.size === 0}
                data-testid="clear-confirm-button"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <Eraser className="mr-2 h-4 w-4" /> Kosongkan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk delete pages dialog — multi-select */}
        <Dialog open={deletePagesDialogOpen} onOpenChange={setDeletePagesDialogOpen}>
          <DialogContent data-testid="delete-pages-dialog" className={editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100" : "border-slate-200 bg-white text-slate-900"}>
            <DialogHeader>
              <DialogTitle>{isEnglish ? "Delete Pages" : "Hapus halaman"}</DialogTitle>
              <DialogDescription>
                {isEnglish ? "Select the pages to delete (you can select multiple). The pages and all their" : "Pilih halaman yang ingin dihapus (boleh lebih dari satu). Halaman beserta seluruh"}
                {isEnglish ? "contents will be permanently deleted. At least 1 page must remain." : "isinya akan dihapus permanen. Minimal harus tersisa 1 halaman."}
              </DialogDescription>
            </DialogHeader>

            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${editorTheme === "dark" ? "border border-[#29466f] bg-[#152541]" : "bg-secondary"}`}>
              <span className="text-sm font-medium">{pages.length} {isEnglish ? "pages" : "halaman"}</span>
              <span className="font-mono-num text-xs text-muted-foreground">
                {deletePagesSet.size} {isEnglish ? "selected" : "dipilih"}
              </span>
            </div>

            {pages.length > 1 && (
              <Button variant="outline" size="sm" className="w-full rounded-lg"
                data-testid="delete-pages-keep-first"
                onClick={() =>
                  setDeletePagesSet(new Set(pages.map((_, i) => i).filter((i) => i !== 0)))
                }>
                {isEnglish ? "Delete all, keep Page 1" : "Hapus semua, sisakan Halaman 1"}
              </Button>
            )}

            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1 hide-dialog-scrollbar">
              {pages.map((pg, i) => {
                const filled = pg.cells.filter((c) => c.type !== "empty").length;
                return (
                  <label
                    key={i}
                    data-testid={`delete-pages-option-${i}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      deletePagesSet.has(i) ? "border-destructive bg-destructive/10" : editorTheme === "dark" ? "border-[#29466f] bg-[#0b1733] hover:bg-[#152541]" : "hover:bg-secondary"
                    }`}
                  >
                    <Checkbox checked={deletePagesSet.has(i)} onCheckedChange={() => toggleDeletePage(i)} />
                    <div className="shrink-0">
                      <MiniPage projectId={id} page={pg} layout={layout} width={40} showCenter={showCenter}
                        centerDotSize={centerDotSize} centerDotColor={centerDotColor} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{isEnglish ? "Page" : "Halaman"} {i + 1}</p>
                      <p className="font-mono-num text-xs text-muted-foreground">{filled}/{layout.CELLS_PER_PAGE} {isEnglish ? "filled" : "terisi"}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {deletePagesSet.size >= pages.length && pages.length > 0 && (
              <p className="text-xs text-[hsl(var(--warn))]" data-testid="delete-pages-warn">
                {isEnglish ? "You cannot delete all pages — keep at least 1." : "Tidak bisa menghapus semua halaman — sisakan minimal 1."}
              </p>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeletePagesDialogOpen(false)}
                data-testid="delete-pages-cancel-button">
                {isEnglish ? "Cancel" : "Batal"}
              </Button>
              <Button onClick={runDeletePages}
                disabled={deletePagesSet.size === 0 || deletePagesSet.size >= pages.length}
                data-testid="delete-pages-confirm-button"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                <Trash2 className="mr-2 h-4 w-4" /> {isEnglish ? "Delete" : "Hapus"} ({deletePagesSet.size})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export / Print page selection dialog */}
        <Dialog open={outputDialogOpen} onOpenChange={setOutputDialogOpen}>
          <DialogContent className={`max-w-lg ${editorTheme === "dark" ? "border-[#29466f] bg-[#07132e] text-slate-100 [&>button]:text-slate-300 [&>button:hover]:bg-[#152541]" : "border-[#bfdbfe] bg-[#eff6ff] text-[#17345d] [&>button]:text-[#17345d] [&>button:hover]:bg-[#dbeafe]"}`} data-testid="output-select-dialog">
            <DialogHeader>
              <DialogTitle>
                {outputMode === "print" ? (isEnglish ? "Print pages" : "Cetak halaman") : (isEnglish ? "Export PDF" : "Ekspor PDF")}
              </DialogTitle>
              <DialogDescription className={editorTheme === "dark" ? "text-slate-400" : "text-[#64748b]"}>
                Pilih halaman yang ingin {outputMode === "print" ? "dicetak" : "diekspor"}.
                Ukuran tetap A4 asli.
              </DialogDescription>
            </DialogHeader>

            <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${editorTheme === "dark" ? "border-[#29466f] bg-[#0b1933] text-slate-100" : "border-[#bfdbfe] bg-[#dbeafe] text-[#17345d]"}`}>
              <label className="flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  data-testid="output-select-all"
                  checked={pages.length > 0 && selectedPageSet.size === pages.length}
                  onCheckedChange={toggleSelectAllPages}
                />
                Pilih semua ({pages.length} halaman)
              </label>
              <span className={editorTheme === "dark" ? "font-mono-num text-xs text-slate-400" : "font-mono-num text-xs text-[#64748b]"}>
                {selectedPageSet.size} dipilih
              </span>
            </div>

            <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {pages.map((pg, i) => {
                const filled = pg.cells.filter((c) => c.type !== "empty").length;
                return (
                  <label
                    key={i}
                    data-testid={`output-page-option-${i}`}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 transition-colors ${
                      editorTheme === "dark" ? "text-slate-100" : "text-[#17345d]"
                    } ${
                      selectedPageSet.has(i)
                        ? (editorTheme === "dark" ? "border-blue-500 bg-blue-500/15" : "border-blue-400 bg-blue-100")
                        : (editorTheme === "dark" ? "border-[#29466f] bg-[#0b1933] hover:bg-[#152541]" : "border-blue-100 bg-white hover:bg-blue-50")
                    }`}
                  >
                    <Checkbox
                      checked={selectedPageSet.has(i)}
                      onCheckedChange={() => togglePageSel(i)}
                    />
                    <div className="shrink-0">
                      <MiniPage projectId={id} page={pg} layout={layout} width={40} showCenter={showCenter} centerDotSize={centerDotSize} centerDotColor={centerDotColor} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Halaman {i + 1}</p>
                      <p className={editorTheme === "dark" ? "font-mono-num text-xs text-slate-400" : "font-mono-num text-xs text-[#64748b]"}>{filled}/{layout.CELLS_PER_PAGE} terisi</p>
                    </div>
                  </label>
                );
              })}
            </div>

            <DialogFooter>
              <Button variant="ghost" className={editorTheme === "dark" ? "text-slate-200 hover:bg-[#152541]" : "text-[#17345d] hover:bg-[#dbeafe]"} onClick={() => setOutputDialogOpen(false)}
                data-testid="output-cancel-button">
                Batal
              </Button>
              <Button className="bg-[#3475e8] text-white hover:bg-[#2563eb]" onClick={confirmOutput} disabled={busy || selectedPageSet.size === 0}
                data-testid="output-confirm-button">
                {outputMode === "print" ? (
                  <><Printer className="mr-2 h-4 w-4" /> Cetak</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Unduh PDF</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
